import { callTool } from "./api";
import { getIdToken, getApiUrl } from "./auth";
import type { Thought, BrainStats, BusActivity, Agent, AnyScope, Scope, ReadScope, PendingItem, AdminOverview, AuditView, IntegrationStatus } from "./brain-types";

// ---------------------------------------------------------------------------
// Admin REST helpers
// ---------------------------------------------------------------------------

export interface AdminProfile {
  userId: string;
  email?: string;
  displayName?: string;
  departments: string[];
  defaultDept?: string;
  role?: string;
  orgMember: boolean;
  lastBriefAt?: number;
  active?: boolean;
  createdAt?: number;
  jiraAccountId?: string;
  personSlug?: string;
}

export interface Invite {
  email: string;
  departments: string[];
  role: string;
  orgMember: boolean;
  displayName?: string;
  invitedBy?: string;
  createdAt?: number;
}

async function adminFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const token = await getIdToken();
  const apiUrl = getApiUrl();
  const res = await fetch(`${apiUrl}${path}`, {
    ...init,
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, ...(init?.headers ?? {}) },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null) as { error?: string } | null;
    throw new Error(body?.error ?? `Admin API error: ${res.status}`);
  }
  return res.json() as Promise<T>;
}

export async function listAdminProfiles(): Promise<AdminProfile[]> {
  const data = await adminFetch<{ profiles: AdminProfile[] }>("/admin/profiles");
  return data.profiles;
}

export async function updateAdminProfile(
  userId: string,
  update: Partial<Pick<AdminProfile, "departments" | "role" | "orgMember" | "displayName" | "email" | "jiraAccountId">>
): Promise<AdminProfile> {
  const data = await adminFetch<{ profile: AdminProfile }>(`/admin/profiles/${encodeURIComponent(userId)}`, {
    method: "PUT",
    body: JSON.stringify(update),
  });
  return data.profile;
}

export async function getAdminOverview(): Promise<AdminOverview> {
  return adminFetch<AdminOverview>("/admin/overview");
}

export async function getAdminIntegrations(): Promise<{ integrations: IntegrationStatus[] }> {
  return adminFetch<{ integrations: IntegrationStatus[] }>("/admin/integrations");
}

export async function getAdminAudit(history = 30): Promise<AuditView> {
  return adminFetch<AuditView>(`/admin/audit?history=${history}`);
}

export async function setProfileActive(userId: string, active: boolean): Promise<AdminProfile | undefined> {
  const data = await adminFetch<{ profile?: AdminProfile }>(`/admin/profiles/${encodeURIComponent(userId)}/status`, {
    method: "POST",
    body: JSON.stringify({ active }),
  });
  return data.profile;
}

export async function listInvites(): Promise<Invite[]> {
  const data = await adminFetch<{ invites: Invite[] }>("/admin/invites");
  return data.invites;
}

/** Result of adding a teammate: provisioned=true means their profile was written
 *  immediately (they exist in the SSO pool); false means an invite was stored. */
export interface AddUserResult { provisioned: boolean; userId?: string; invite?: Invite }

export async function createInvite(input: {
  email: string; departments?: string[]; role?: string; orgMember?: boolean; displayName?: string;
}): Promise<AddUserResult> {
  return adminFetch<AddUserResult>("/admin/invites", { method: "POST", body: JSON.stringify(input) });
}

export async function revokeInvite(email: string): Promise<void> {
  await adminFetch(`/admin/invites/${encodeURIComponent(email)}`, { method: "DELETE" });
}

// --- Customer registry (Customer Library) ----------------------------------

export type CustomerStatus = "prospect" | "active" | "churned";

export interface Customer {
  customerId: string;
  name: string;
  status: CustomerStatus;
  tier?: string;
  product: string[];
  csm?: string;
  aliases: string[];
  domains: string[];
  modules: string[];
  /** Short prose: who they are, where they're located, what they do. */
  summary?: string;
  /** City/region, e.g. "Auckland, NZ". */
  location?: string;
  /** IANA timezone, e.g. "Pacific/Auckland". */
  timezone?: string;
  completeness?: number;
  /** OVERRIDE ONLY — JSM org is auto-resolved from aliases server-side. */
  jiraOrgId?: number;
  // --- registry superset (Aaron-registry lossless fields) ---
  crmCompanyId?: number;
  legalName?: string;
  primaryContact?: string;
  startedAt?: string;
  stub?: boolean;
  notes?: string;
  /** Install plane — the customer's live instance (app_url/host/db/connect). */
  install?: Record<string, unknown>;
  infra?: Record<string, unknown>;
  review?: { lastReviewedAt?: string; reviewedBy?: string; cadenceDays?: number };
  /** Whole-customer integrations knowledge sign-off. */
  integrationsReview?: { reviewedAt?: string; reviewedBy?: string; notes?: string };
  createdAt?: number;
  updatedAt?: number;
}

export async function listCustomers(): Promise<Customer[]> {
  const data = await adminFetch<{ customers: Customer[] }>("/admin/customers");
  return data.customers;
}

export async function getCustomer(customerId: string): Promise<Customer> {
  const data = await adminFetch<{ customer: Customer }>(`/admin/customers/${encodeURIComponent(customerId)}`);
  return data.customer;
}

export async function saveCustomer(input: Partial<Customer> & { customerId: string }): Promise<Customer> {
  const data = await adminFetch<{ customer: Customer }>("/admin/customers", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return data.customer;
}

export async function deleteCustomerRecord(customerId: string): Promise<void> {
  await adminFetch(`/admin/customers/${encodeURIComponent(customerId)}`, { method: "DELETE" });
}

export type LiveTriState = "unknown" | "yes" | "no";
export type DocKind = "docs" | "code" | "runbooks" | "sops";

export interface DocKindCounts {
  docs: number;
  code: number;
  runbooks: number;
  sops: number;
}

export interface CustomerIntegrationDocument {
  key: string;
  name: string;
  kind?: DocKind;
  size?: number;
  lastModified?: string;
}

export interface CustomerIntegrationCard {
  featureKey: string;
  docsPrefix: string;
  assigned: LiveTriState;
  deployed: LiveTriState;
  summary?: string;
  documents: CustomerIntegrationDocument[];
  docCounts?: DocKindCounts;
}

export interface IntegrationEvidence {
  source: "confluence" | "aws" | "gitlab" | "manual";
  ref: string;
  note?: string;
  seenAt: string;
}

export interface IntegrationLastScan {
  scannedAt: string;
  signal: string;
  score: number;
  found: string[];
  missing: string[];
}

export interface IntegrationVerification {
  verifiedAt: string;
  verifiedBy: string;
  context?: string;
  notes?: string;
}

export interface IntegrationRecord {
  customerId: string;
  integrationKey: string;
  name: string;
  status: "draft" | "confirmed" | "retired";
  deployed: "unknown" | "deployed" | "not-deployed";
  summary: string | null;
  purpose: string | null;
  builtBy: string | null;
  builtAt: string | null;
  endpoints: string[] | null;
  dependencies: string[] | null;
  migrationImpact: string | null;
  runbookKey: string | null;
  evidence: IntegrationEvidence[];
  lastScan: IntegrationLastScan | null;
  lastVerification: IntegrationVerification | null;
  createdAt: string;
  updatedAt: string;
  updatedBy: string;
}

export interface FleetBox {
  instanceId: string;
  os?: string;
  python?: string;
  mysql?: string;
}

export interface FleetAliasState {
  alias: string;
  observedAt?: string;
  stack: {
    roxen?: string;
    fastapi?: string;
    fastapiCommit?: string;
    react?: string;
    reactUnstamped?: boolean;
    reactDeployedAt?: string;
    siteStatus?: string;
    heartbeat?: string;
    profile: string;
  };
  /** service name → "present" | "partial". Missing = not observed this run. */
  services: Record<string, string>;
  boxes: FleetBox[];
}

export interface FleetStateBlock {
  available: boolean;
  aliases: FleetAliasState[];
}

export interface CustomerIntegrationsView {
  customerId: string;
  signOffStatus: "UNKNOWN" | "reviewed";
  signOff?: { reviewedAt?: string; reviewedBy?: string; notes?: string };
  liveSource: "unavailable" | "customer-services" | "fleet-state";
  liveWarning?: string;
  features: CustomerIntegrationCard[];
  records?: IntegrationRecord[];
  draftCount?: number;
  fleet?: FleetStateBlock;
}

export interface IntegrationTemplate {
  integrationKey: string;
  name: string;
  description: string | null;
  defaultMigrationImpact: string | null;
  resources: Array<{
    type: string;
    name_pattern?: string;
    arn?: string;
    check?: { enabled?: boolean };
    description?: string;
  }>;
  enabled: boolean;
  updatedAt: string;
  updatedBy: string;
}

export interface ScanSummary {
  scannedAt: string;
  dryRun: boolean;
  customers: Array<{
    customerId: string;
    detections: Array<{
      integrationKey: string;
      name: string;
      signal: string;
      score: number;
      found: string[];
      missing: string[];
      resources: Array<{
        type: string;
        name_pattern?: string;
        check_status: string;
        check_message: string;
        resolved_name: string;
        confirmed_arn: string | null;
      }>;
    }>;
    upserted: string[];
    drifted: string[];
    error?: string;
  }>;
  errors: string[];
}

export async function getCustomerIntegrations(customerId: string): Promise<CustomerIntegrationsView> {
  return adminFetch<CustomerIntegrationsView>(
    `/admin/customers/${encodeURIComponent(customerId)}/integrations`,
  );
}

export async function signOffCustomerIntegrations(
  customerId: string,
  notes?: string,
): Promise<CustomerIntegrationsView> {
  return adminFetch<CustomerIntegrationsView>(
    `/admin/customers/${encodeURIComponent(customerId)}/integrations/sign-off`,
    { method: "POST", body: JSON.stringify({ notes }) },
  );
}

export async function putCustomerIntegrationRecord(
  customerId: string,
  integrationKey: string,
  body: Record<string, unknown>,
): Promise<IntegrationRecord> {
  return adminFetch<IntegrationRecord>(
    `/admin/customers/${encodeURIComponent(customerId)}/integrations/${encodeURIComponent(integrationKey)}`,
    { method: "PUT", body: JSON.stringify(body) },
  );
}

export async function deleteCustomerIntegrationRecord(
  customerId: string,
  integrationKey: string,
): Promise<void> {
  await adminFetch(
    `/admin/customers/${encodeURIComponent(customerId)}/integrations/${encodeURIComponent(integrationKey)}`,
    { method: "DELETE" },
  );
}

export async function verifyCustomerIntegrationRecord(
  customerId: string,
  integrationKey: string,
  body: { context?: string; notes?: string } = {},
): Promise<IntegrationRecord> {
  return adminFetch<IntegrationRecord>(
    `/admin/customers/${encodeURIComponent(customerId)}/integrations/${encodeURIComponent(integrationKey)}/verify`,
    { method: "POST", body: JSON.stringify(body) },
  );
}

export async function listIntegrationTemplates(enabledOnly = false): Promise<IntegrationTemplate[]> {
  const q = enabledOnly ? "?enabledOnly=1" : "";
  return adminFetch<IntegrationTemplate[]>(`/admin/integration-templates${q}`);
}

export async function putIntegrationTemplate(
  integrationKey: string,
  body: Record<string, unknown>,
): Promise<IntegrationTemplate> {
  return adminFetch<IntegrationTemplate>(
    `/admin/integration-templates/${encodeURIComponent(integrationKey)}`,
    { method: "PUT", body: JSON.stringify(body) },
  );
}

export async function deleteIntegrationTemplate(integrationKey: string): Promise<void> {
  await adminFetch(`/admin/integration-templates/${encodeURIComponent(integrationKey)}`, {
    method: "DELETE",
  });
}

export async function scanCustomerIntegrationsApi(body: {
  customerId?: string;
  dryRun?: boolean;
}): Promise<ScanSummary> {
  return adminFetch<ScanSummary>(`/admin/integrations/scan`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

// --- Knowledge Weather (Phase E3) ------------------------------------------

export type Weather = "green" | "amber" | "red" | "grey";
export interface WeatherNode {
  nodeKey: string;
  entity?: string;
  id?: string;
  name?: string;
  atomCount?: number;
  weather?: Weather;
}

export async function getWeather(): Promise<Record<Weather, WeatherNode[]>> {
  const result = await callTool("get_weather", { _format: "json" });
  const w = parseJson<{ weather: Partial<Record<Weather, WeatherNode[]>> }>("get_weather", result).weather ?? {};
  return { green: w.green ?? [], amber: w.amber ?? [], red: w.red ?? [], grey: w.grey ?? [] };
}

// --- Standup Brain tab (docs/standup/03-brain-tab.md) ----------------------

export type QuestionKind = "contradiction" | "confusion" | "stale" | "gap";

export interface StandupQuestion {
  question_id: string;
  kind: QuestionKind;
  text: string;
}

export interface StandupBrief {
  questions: StandupQuestion[];
  stale_reviews: number;
  oldest_pending_age_h: number;
  health: string | null;
}

export async function getStandupBrief(): Promise<StandupBrief> {
  const result = await callTool("standup_brief", { _format: "json" });
  const data = parseJson<Partial<StandupBrief>>("standup_brief", result);
  return {
    questions: Array.isArray(data.questions) ? data.questions : [],
    stale_reviews: data.stale_reviews ?? 0,
    oldest_pending_age_h: data.oldest_pending_age_h ?? 0,
    health: data.health ?? null,
  };
}

export async function answerQuestion(questionId: string, answerText: string, scope?: string): Promise<string> {
  return callTool("answer_question", { question_id: questionId, answer_text: answerText, ...(scope ? { scope } : {}) });
}

export async function dismissQuestion(questionId: string, reason?: string): Promise<string> {
  return callTool("dismiss_question", { question_id: questionId, ...(reason ? { reason } : {}) });
}

// --- Contradiction resolver (Phase D6) -------------------------------------

export interface ContradictionAtom {
  key: string;
  content: string;
  scope?: string;
  type?: string;
  created_at?: number;
  source_url?: string;
}
export interface Contradiction {
  questionId: string;
  text: string;
  createdAt: number;
  atoms: ContradictionAtom[];
}

export async function listContradictions(): Promise<Contradiction[]> {
  const result = await callTool("list_contradictions", { _format: "json" });
  return parseJson<{ contradictions: Contradiction[] }>("list_contradictions", result).contradictions;
}

// --- Standup priorities (Phase D5) -----------------------------------------

export async function getPriorities(): Promise<string[]> {
  const data = await adminFetch<{ priorities: string[] }>("/admin/priorities");
  return data.priorities;
}

export async function setPriorities(priorities: string[]): Promise<string[]> {
  const data = await adminFetch<{ priorities: string[] }>("/admin/priorities", {
    method: "POST",
    body: JSON.stringify({ priorities }),
  });
  return data.priorities;
}

// --- Content store / S3 files (Phase D3) -----------------------------------

export interface StoreObject {
  key: string;
  size?: number;
  lastModified?: string;
}
export interface StoreTags {
  project?: string;
  customer?: string;
  dept?: string;
  source?: string;
  tier?: string;
}

export async function listFiles(prefix: string): Promise<StoreObject[]> {
  const data = await adminFetch<{ objects: StoreObject[] }>(`/admin/files?prefix=${encodeURIComponent(prefix)}`);
  return data.objects;
}

export async function presignFile(key: string): Promise<string> {
  const data = await adminFetch<{ url: string }>(`/admin/files/presign?key=${encodeURIComponent(key)}`);
  return data.url;
}

/** Fetch text/markdown/html via same-origin admin proxy (avoids browser→S3 CORS). */
export async function getObjectText(key: string): Promise<string> {
  const data = await adminFetch<{ content: string }>(`/admin/files/text?key=${encodeURIComponent(key)}`);
  return data.content ?? "";
}

export async function tagFile(key: string, tags: StoreTags): Promise<void> {
  await adminFetch("/admin/files/tag", { method: "POST", body: JSON.stringify({ key, tags }) });
}

export async function moveFile(fromKey: string, toKey: string): Promise<void> {
  await adminFetch("/admin/files/move", { method: "POST", body: JSON.stringify({ fromKey, toKey }) });
}

export async function deleteFile(key: string): Promise<void> {
  await adminFetch("/admin/files/delete", { method: "POST", body: JSON.stringify({ key }) });
}

export async function createFolder(prefix: string, tags: StoreTags): Promise<void> {
  await adminFetch("/admin/files/folder", { method: "POST", body: JSON.stringify({ prefix, tags }) });
}

export async function removeFolder(prefix: string): Promise<void> {
  await adminFetch("/admin/files/folder", { method: "POST", body: JSON.stringify({ prefix, remove: true }) });
}

function toBase64(bytes: Uint8Array): string {
  let bin = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    bin += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(bin);
}

export interface NoteVersion {
  versionId: string;
  lastModified?: string;
  size?: number;
  isLatest?: boolean;
}

export async function getNote(key: string, versionId?: string): Promise<{ content: string; versions?: NoteVersion[] }> {
  const q = `?key=${encodeURIComponent(key)}${versionId ? `&versionId=${encodeURIComponent(versionId)}` : ""}`;
  return adminFetch<{ content: string; versions?: NoteVersion[] }>(`/admin/notes${q}`);
}

export async function saveNote(key: string, content: string, tags: StoreTags): Promise<{ key: string; versions: NoteVersion[] }> {
  return adminFetch<{ key: string; versions: NoteVersion[] }>("/admin/notes", {
    method: "POST",
    body: JSON.stringify({ key, content, tags }),
  });
}

export async function uploadFile(key: string, file: File, tags: StoreTags): Promise<void> {
  const buf = new Uint8Array(await file.arrayBuffer());
  await adminFetch("/admin/files", {
    method: "POST",
    body: JSON.stringify({ key, contentBase64: toBase64(buf), contentType: file.type || "application/octet-stream", tags }),
  });
}

// --- Customer meetings (Phase 1) — on-site recorder + audit ----------------

export type MeetingSource = "onsite" | "meet";
export type MeetingStatus =
  | "recording" | "uploading" | "transcribing" | "synthesizing" | "ready" | "audited" | "error";

export interface Meeting {
  id: string;
  customerId: string;
  visitId: string;
  title: string;
  date: string;
  source: MeetingSource;
  status: MeetingStatus;
  error?: string | null;
  attendees: string[];
  recordedBy?: string;
  durationSec?: number;
  chunkCount?: number;
  audioMimeType?: string;
  createdAt: string;
  updatedAt: string;
}

export interface MeetingDetail {
  meeting: Meeting;
  notes: string;
  transcript: string;
  aiDraft: string;
}

export async function listMeetings(customerId: string, visitId: string): Promise<Meeting[]> {
  const data = await adminFetch<{ meetings: Meeting[] }>(
    `/meetings?customer=${encodeURIComponent(customerId)}&visit=${encodeURIComponent(visitId)}`
  );
  return data.meetings;
}

export async function getMeeting(customerId: string, visitId: string, meetingId: string): Promise<MeetingDetail> {
  return adminFetch<MeetingDetail>(
    `/meetings/detail?customer=${encodeURIComponent(customerId)}&visit=${encodeURIComponent(visitId)}&meeting=${encodeURIComponent(meetingId)}`
  );
}

export async function startMeeting(input: {
  customerId: string;
  visitId: string;
  title?: string;
  attendees?: string[];
  audioMimeType?: string;
}): Promise<{ meetingId: string; meeting: Meeting }> {
  return adminFetch("/meetings/start", { method: "POST", body: JSON.stringify(input) });
}

export async function uploadMeetingChunk(input: {
  customerId: string;
  visitId: string;
  meetingId: string;
  seq: number;
  contentBase64: string;
  contentType?: string;
}): Promise<{ ok: boolean; seq: number }> {
  return adminFetch("/meetings/chunk", { method: "POST", body: JSON.stringify(input) });
}

export async function finalizeMeeting(input: {
  customerId: string;
  visitId: string;
  meetingId: string;
  title?: string;
  attendees?: string[];
  audioMimeType?: string;
  chunkCount?: number;
  durationSec?: number;
}): Promise<{ ok: boolean; meetingId: string }> {
  return adminFetch("/meetings/finalize", { method: "POST", body: JSON.stringify(input) });
}

export async function auditMeeting(input: {
  customerId: string;
  visitId: string;
  meetingId: string;
  notesMarkdown: string;
}): Promise<{ ok: boolean }> {
  return adminFetch("/meetings/audit", { method: "POST", body: JSON.stringify(input) });
}

export async function draftMeetingReport(input: {
  customerId: string;
  visitId: string;
  meetingId: string;
}): Promise<{ url: string }> {
  return adminFetch("/meetings/report", { method: "POST", body: JSON.stringify(input) });
}

// --- Transcript import (plan 2026-07-21) — file drop / Meet URL / Doc URL ---

export type ImportSource = "file" | "meet-url" | "doc-url";

export interface ImportPreview {
  title: string;
  date: string;
  attendees: string[];
  transcriptText: string;
  googleSummaryText?: string;
}

export async function previewMeetingImport(input: {
  customerId: string;
  visitId?: string;
  source: ImportSource;
  transcriptText?: string;
  googleSummaryText?: string;
  url?: string;
}): Promise<ImportPreview> {
  return adminFetch<ImportPreview>("/meetings/import/preview", { method: "POST", body: JSON.stringify(input) });
}

export async function importMeeting(input: {
  customerId: string;
  visitId?: string;
  title: string;
  date: string;
  attendees: string[];
  transcriptText: string;
  googleSummaryText?: string;
}): Promise<{ meetingId: string; meeting: Meeting; visitId: string }> {
  return adminFetch("/meetings/import", { method: "POST", body: JSON.stringify(input) });
}

// --- Site visits (DO-2298 pivot) — the container for meetings + discussion ---
// items + actions. Types mirror server/src/services/visits.ts verbatim (camelCase).

export type VisitType = "onsite" | "remote";
export type VisitStatus = "open" | "ready" | "audited";
/** WP-H review-state model: AI rows are born `proposed` (invisible in the record
 *  until accepted); human rows are born `accepted`; `dismissed` rows are kept soft. */
export type ReviewState = "proposed" | "accepted" | "dismissed";

export interface Visit {
  customerId: string;
  id: string;
  title: string;
  type: VisitType;
  status: VisitStatus;
  startsOn: string;
  endsOn: string | null;
  summary: string | null;
  notesNextVisit: string | null;
  imported: boolean;
  sourcePageId: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface VisitListItem extends Visit {
  meetingCount: number;
  openItemCount: number;
}

export type ItemPriority = "high" | "medium" | "low";
export type ItemDisposition = "raise" | "raise-fr" | "road-map-fr" | "discuss" | "linked" | "resolved";
export type ItemStatus = "open" | "investigate" | "with-client" | "needs-discussion" | "in-ticket" | "done" | "dropped";

export interface DiscussionItem {
  id: number;
  customerId: string;
  visitId: string;
  n: number;
  priority: ItemPriority | null;
  topic: string;
  notes: string | null;
  disposition: ItemDisposition;
  status: ItemStatus;
  jiraKey: string | null;
  sourceMeetingId: string | null;
  carriedFromItemId: number | null;
  createdBy: "ai" | "human" | "import";
  audited: boolean;
  reviewState: ReviewState;
  evidence: string | null;
  evidenceTs: string | null;
  carryHintItemId: number | null;
}

/** Live Jira state for a raised action — hydrated server-side on /visits/detail. */
export interface ActionJira {
  key: string;
  summary: string;
  status: string;
  statusCategory: string;
  assignee: string | null;
}

export interface VisitAction {
  id: number;
  customerId: string;
  visitId: string;
  owner: string | null;
  text: string;
  dueOn: string | null;
  done: boolean;
  sourceMeetingId: string | null;
  jiraKey: string | null;
  reviewState: ReviewState;
  evidence: string | null;
  evidenceTs: string | null;
  suggestedTarget: string | null;
  /** Jira is the source of truth for actions — null when never raised or hydrate failed. */
  jira?: ActionJira | null;
}

export interface VisitDetailResponse {
  visit: Visit;
  meetings: Meeting[];
  items: DiscussionItem[];
  actions: VisitAction[];
  carryOver: DiscussionItem[];
  previousVisitId: string | null;
}

export async function listVisits(customerId: string): Promise<VisitListItem[]> {
  const data = await adminFetch<{ visits: VisitListItem[] }>(`/visits?customer=${encodeURIComponent(customerId)}`);
  return data.visits;
}

export async function createVisit(input: {
  customerId: string;
  title?: string;
  type?: VisitType;
  date?: string;
}): Promise<Visit> {
  const data = await adminFetch<{ visitId: string; visit: Visit }>("/visits", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return data.visit;
}

export async function getVisitDetail(customerId: string, visitId: string): Promise<VisitDetailResponse> {
  return adminFetch<VisitDetailResponse>(
    `/visits/detail?customer=${encodeURIComponent(customerId)}&visit=${encodeURIComponent(visitId)}`
  );
}

export async function patchVisit(input: {
  customerId: string;
  visitId: string;
  title?: string;
  summary?: string;
  notesNextVisit?: string;
  endsOn?: string;
}): Promise<Visit> {
  const data = await adminFetch<{ visit: Visit }>("/visits", { method: "PATCH", body: JSON.stringify(input) });
  return data.visit;
}

/** Task 17 — freezes the live visit state (meetings, items, actions, tickets)
 *  into an HTML report snapshot under `customers/{c}/visits/{v}/reports/`, and
 *  returns a short-lived presigned URL to it. */
export async function exportVisit(
  input: { customerId: string; visitId: string }
): Promise<{ url: string; pdfUrl: string | null }> {
  return adminFetch<{ url: string; pdfUrl: string | null }>("/visits/export", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

/** Task 17 — marks the visit `audited`, publishes its summary to the brain
 *  (company scope), and deletes the raw meeting audio under the visit. */
export async function auditVisit(input: { customerId: string; visitId: string }): Promise<Visit> {
  const data = await adminFetch<{ visit: Visit }>("/visits/audit", { method: "POST", body: JSON.stringify(input) });
  return data.visit;
}

/** Admin-only — permanently deletes a site visit and every artifact under it
 *  (meetings, discussion items, actions, reports, files). 403 for non-admins. */
export async function deleteVisit(input: { customerId: string; visitId: string }): Promise<void> {
  await adminFetch("/visits", { method: "DELETE", body: JSON.stringify(input) });
}

export async function createDiscussionItem(input: {
  customerId: string;
  visitId: string;
  topic: string;
  notes?: string;
  priority?: ItemPriority;
  disposition?: ItemDisposition;
  sourceMeetingId?: string;
}): Promise<DiscussionItem> {
  const data = await adminFetch<{ item: DiscussionItem }>("/visits/items", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return data.item;
}

export async function patchDiscussionItem(
  id: number,
  patch: Partial<Pick<DiscussionItem, "topic" | "notes" | "priority" | "disposition" | "status" | "jiraKey" | "audited" | "reviewState">>
): Promise<DiscussionItem> {
  const data = await adminFetch<{ item: DiscussionItem }>("/visits/items", {
    method: "PATCH",
    body: JSON.stringify({ id, ...patch }),
  });
  return data.item;
}

// --- Raise-ticket (Task 15 — mirrors server/src/constants/raise-config.ts RAISE_TARGETS) --

export type RaiseTarget = "support" | "feature-request" | "dev";

export interface RaiseItemResult {
  key: string;
  url: string;
  /** null on a rare race (item vanished between the existence check and the
   *  post-create patch) — the Jira issue was still created; null-check before
   *  reading item.jiraKey. */
  item: DiscussionItem | null;
  note?: string;
  warning?: string;
}

export async function raiseItemTicket(input: {
  customerId: string;
  visitId: string;
  itemId: number;
  target: RaiseTarget;
  summary: string;
  description: string;
  priority?: string;
}): Promise<RaiseItemResult> {
  return adminFetch<RaiseItemResult>("/visits/items/raise", { method: "POST", body: JSON.stringify(input) });
}

export async function linkItemTicket(input: { itemId: number; jiraKey: string }): Promise<{ item: DiscussionItem }> {
  return adminFetch<{ item: DiscussionItem }>("/visits/items/link", { method: "POST", body: JSON.stringify(input) });
}

export interface RaiseActionResult {
  key: string;
  url: string;
  action: VisitAction | null;
  note?: string;
  warning?: string;
}

export async function raiseActionTicket(input: {
  customerId: string;
  visitId: string;
  actionId: number;
  target: RaiseTarget;
  summary: string;
  description: string;
  priority?: string;
}): Promise<RaiseActionResult> {
  return adminFetch<RaiseActionResult>("/visits/actions/raise", { method: "POST", body: JSON.stringify(input) });
}

export async function linkActionTicket(input: {
  actionId: number;
  jiraKey: string;
}): Promise<{ action: VisitAction }> {
  return adminFetch<{ action: VisitAction }>("/visits/actions/link", { method: "POST", body: JSON.stringify(input) });
}

export async function acceptCarryOverItem(input: {
  customerId: string;
  visitId: string;
  fromItemId: number;
}): Promise<DiscussionItem> {
  const data = await adminFetch<{ item: DiscussionItem }>("/visits/items/accept-carryover", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return data.item;
}

export async function createVisitAction(input: {
  customerId: string;
  visitId: string;
  text: string;
  owner?: string;
  dueOn?: string;
  sourceMeetingId?: string;
}): Promise<VisitAction> {
  const data = await adminFetch<{ action: VisitAction }>("/visits/actions", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return data.action;
}

export async function patchVisitAction(
  id: number,
  patch: Partial<Pick<VisitAction, "owner" | "text" | "dueOn" | "done" | "jiraKey" | "reviewState">>
): Promise<VisitAction> {
  const data = await adminFetch<{ action: VisitAction }>("/visits/actions", {
    method: "PATCH",
    body: JSON.stringify({ id, ...patch }),
  });
  return data.action;
}

// --- Customer tickets (Task 11 — mirrors GET /tickets?customer=&kind= from Task 10) --

export type OrgIssueKind = "tickets" | "roadmap";
export type TicketStatusCategory = "new" | "indeterminate" | "done";

export interface Ticket {
  key: string;
  summary: string;
  status: string;
  statusCategory: TicketStatusCategory;
  assignee: string | null;
  priority: string | null;
  updated: string;
  url: string;
}

export interface TicketCounts {
  open: number;
  inProgress: number;
  done30d: number;
}

export interface CustomerTickets {
  /** null = no Jira org mapped on the customer record — show the no-mapping state, not an error. */
  orgId: number | null;
  kind: OrgIssueKind;
  counts: TicketCounts;
  tickets: Ticket[];
}

export async function getCustomerTickets(
  customerId: string,
  kind: OrgIssueKind = "tickets"
): Promise<CustomerTickets> {
  const q = new URLSearchParams({ customer: customerId, kind });
  return adminFetch<CustomerTickets>(`/tickets?${q.toString()}`);
}

export interface JsmOrganizationOption {
  id: number;
  name: string;
}

/** Admin: JSM organizations for the customer jiraOrgId picker. */
export async function listJsmOrganizations(): Promise<JsmOrganizationOption[]> {
  const data = await adminFetch<{ organizations: JsmOrganizationOption[] }>("/admin/jira/organizations");
  return data.organizations ?? [];
}

// --- Ticket-analysis artifact (docs/design/2026-07-17-tickets-intelligence.md) ---
// THE API CONTRACT for the nightly ticket-analysis job. The tab renders a stored
// artifact instantly; server (jobs/ticket-analysis.ts) produces it. `roadmapSignals`
// is computed in the same LLM pass and consumed by the Roadmap tab (Phase 4).

export type FrictionLevel = "calm" | "normal" | "elevated" | "hot";
export type Trend = "up" | "down" | "flat";

export interface TicketRef {
  text: string;
  refs: string[]; // [ref:ticket:KEY] / [ref:visit:...] markers backing the claim
}

export interface TicketTheme {
  id: string; // STABLE across runs so trends work
  name: string;
  openCount: number;
  totalCount: number;
  trend: Trend;
  since: string;
  synopsis: string;
  ticketKeys: string[];
}

export interface TicketAnalysis {
  customerId: string;
  generatedAt: string; // ISO — drives the freshness stamp
  summary: { text: string; refs: string[] };
  volume: { months: { month: string; count: number }[]; baseline: number; anomaly?: string };
  responsiveness: {
    firstResponseHours: number;
    firstResponseTrendPct: number;
    resolutionDays: number;
    resolutionTrendPct: number;
  };
  waitingOn: { ourCourt: number; withClient: number; staleBothSides: number; withClientOver21d: number };
  friction: { level: FrictionLevel; reasons: TicketRef[] };
  themes: TicketTheme[];
  /** Demand-signal flags for the Roadmap tab (Phase 4) — same LLM pass. */
  roadmapSignals?: TicketRef[];
}

/** Latest cached ticket-analysis artifact for a customer. 404 → null (no analysis yet). */
export async function getTicketAnalysis(customerId: string): Promise<TicketAnalysis | null> {
  try {
    return await adminFetch<TicketAnalysis>(`/tickets/analysis?customer=${encodeURIComponent(customerId)}`);
  } catch (e) {
    if (e instanceof Error && /404|not found/i.test(e.message)) return null;
    throw e;
  }
}

/** Trigger an async re-analysis (mirrors /finance/refresh). 202 → true. */
export async function refreshTicketAnalysis(customerId: string): Promise<boolean> {
  await adminFetch("/tickets/analysis/refresh", { method: "POST", body: JSON.stringify({ customer: customerId }) });
  return true;
}

// --- Roadmap / Flightpath (docs/design/2026-07-17-tickets-intelligence.md §Roadmap,
// 04-roadmap-tab.md). The factory Jira fields don't exist yet; the server adapter
// derives best-effort values from today's Jira and marks them `derived`.

export type RoadmapStage = "requested" | "order-desk" | "laned" | "on-train" | "delivered";
export type RoadmapLane = "fast-track" | "roadmap" | "charter";

export interface RoadmapRequest {
  key: string; // TICKET-xxxxx or works-order key
  title: string; // publicTitle when real, summary when derived
  stage: RoadmapStage;
  lane?: RoadmapLane;
  votes?: number; // linked-ticket count when >1
  fixVersion?: string; // "4.16"
  shipDate?: string;
  daysInStage: number;
  hero?: boolean;
  derived: string[]; // which fields were inferred, [] when clean
}

export interface RoadmapDecision {
  key: string;
  title: string;
  lanesOffered: string[];
  daysSinceLetter: number;
}

export interface RoadmapView {
  customerId: string;
  counts: {
    total: number;
    awaitingDecision: number;
    oldestAwaitingDays: number;
    fastTracked: number;
    onRoadmap: number;
    delivered: number;
  };
  requests: RoadmapRequest[];
  decisionQueue: RoadmapDecision[];
  signals?: { text: string; refs: string[] }[]; // AI demand-signal flags (from the ticket-analysis artifact)
}

/** Latest roadmap view for a customer. 404 → null (no mapping / adapter unavailable). */
export async function getRoadmap(customerId: string): Promise<RoadmapView | null> {
  try {
    return await adminFetch<RoadmapView>(`/roadmap?customer=${encodeURIComponent(customerId)}`);
  } catch (e) {
    if (e instanceof Error && /404|not found/i.test(e.message)) return null;
    throw e;
  }
}

// --- Attention feed / Today cockpit (docs/design/2026-07-17-today-cockpit.md, C1) ---

export type FeedKind = "money" | "ops" | "promises" | "knowledge" | "relationships";
export type FeedSeverity = "red" | "warn" | "info";
export type FeedStatus = "new" | "seen" | "snoozed" | "done";

export interface FeedItem {
  itemId: string;
  kind: FeedKind;
  severity: FeedSeverity;
  title: string;
  detail: string;
  refs: string[];
  seedPrompt: string;
  status: FeedStatus;
  firstSeen: number;
  source: string;
  updatedAt: number;
}

export interface FeedHeartbeat {
  job: string;
  ok: boolean | null;
  ts: number | null;
}

export interface FeedView {
  items: FeedItem[];
  heartbeat: FeedHeartbeat[];
  brief: string;
}

/** GET /feed — open items + ops heartbeat + brief line (single page-load call). */
export async function getFeed(): Promise<FeedView> {
  return adminFetch<FeedView>("/feed");
}

/** POST /feed/{itemId} — change an item's status (severity identifies its row). */
export async function patchFeedItem(
  itemId: string,
  severity: FeedSeverity,
  status: FeedStatus,
  note?: string,
): Promise<void> {
  await adminFetch(`/feed/${encodeURIComponent(itemId)}`, {
    method: "POST",
    body: JSON.stringify({ status, severity, note }),
  });
}

// --- Project registry (Phase D1) — mirrors the Customer helpers ------------

export type ProjectKind = "internal" | "customer";
/** Single gated workflow status (P2). Blocked is a flag, not a status. */
export type ProjectStatus =
  | "draft"
  | "planning"
  | "in_progress"
  | "on_hold"
  | "with_client"
  | "done"
  | "archived";

export const PROJECT_STATUSES: readonly ProjectStatus[] = [
  "draft", "planning", "in_progress", "on_hold", "with_client", "done", "archived",
] as const;

/** `on_hold` counts as open — paused work is unfinished, not gone. */
export function isOpenProjectStatus(s: ProjectStatus | string | undefined): boolean {
  return s === "planning" || s === "in_progress" || s === "on_hold" || s === "with_client";
}

// Labels only — `done` reads as "Delivered" (Isaac 2026-08-13) while the stored
// value stays `done`, so no data migration and no `status === "done"` rewrites.
export const PROJECT_STATUS_LABELS: Record<ProjectStatus, string> = {
  draft: "Draft",
  planning: "Planning",
  in_progress: "In progress",
  on_hold: "On hold",
  with_client: "With client",
  done: "Delivered",
  archived: "Archived",
};

export type JiraScope =
  | { type: "epic"; key: string }
  | { type: "fixVersion"; project: string; version: string }
  | { type: "label"; label: string }
  | { type: "project"; key: string };

/** A scope plus the workstream name it contributes to matched rows (D1-a). */
export interface NamedScope {
  name: string;
  scope: JiraScope;
}

export interface ProjectLinks {
  jira_epic?: string;
  slack_channel?: string;
  confluence_space?: string;
  repos: string[];
  jira_scope?: JiraScope;
  /** Multi-board scopes — a project can span several Jira boards. */
  jira_scopes?: NamedScope[];
  /** Optional release train (e.g. "4.16"). */
  fix_version?: string;
}

export interface Project {
  projectId: string;
  name: string;
  kind: ProjectKind;
  customer?: string;
  template?: string;
  status: ProjectStatus;
  blocked?: boolean;
  blockedReason?: string;
  external?: boolean;
  owner?: string;
  deputy?: string;
  team?: string;
  systems: string[];
  links: ProjectLinks;
  jiraScope?: JiraScope;
  /** Named multi-board scopes — mirrors links.jira_scopes (D1-a). */
  jiraScopes?: NamedScope[];
  templateFields: Record<string, unknown>;
  tags: string[];
  completeness?: number;
  // --- registry superset + work-board fields (Aaron registry 2026-07) ---
  description?: string;
  startedAt?: string;
  shippedAt?: string;
  priority?: string;
  assignee?: string;
  reporter?: string;
  nextAction?: string;
  workStreams?: string[];
  jiraTickets?: string[];
  /** Optional release train (e.g. "4.16") — stored in links.fix_version. */
  fixVersion?: string;
  review?: { lastReviewedAt?: string; reviewedBy?: string; cadenceDays?: number };
  createdAt?: number;
  updatedAt?: number;
}

export async function listProjects(customerId?: string): Promise<Project[]> {
  const q = customerId ? `?customer=${encodeURIComponent(customerId)}` : "";
  const data = await adminFetch<{ projects: Project[] }>(`/admin/projects${q}`);
  return data.projects;
}

export async function getProject(projectId: string): Promise<Project> {
  const data = await adminFetch<{ project: Project }>(`/admin/projects/${encodeURIComponent(projectId)}`);
  return data.project;
}

export async function saveProject(input: Partial<Project> & { projectId: string }): Promise<Project> {
  const data = await adminFetch<{ project: Project }>("/admin/projects", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return data.project;
}

export async function deleteProjectRecord(projectId: string): Promise<void> {
  await adminFetch(`/admin/projects/${encodeURIComponent(projectId)}`, { method: "DELETE" });
}

// --- Status workflow (P2) ---------------------------------------------------

export interface GateCheck {
  key: string;
  label: string;
  ok: boolean;
  advisory?: boolean;
  detail?: string;
}

export interface TransitionCheck {
  from: ProjectStatus;
  to: ProjectStatus;
  allowed: boolean;
  checks: GateCheck[];
}

export async function getTransitionChecks(projectId: string, to: ProjectStatus): Promise<TransitionCheck> {
  return adminFetch<TransitionCheck>(
    `/admin/projects/${encodeURIComponent(projectId)}/transition-checks?to=${encodeURIComponent(to)}`
  );
}

/** 409s (with checks) when a gate fails and override is not set. */
export async function transitionProject(
  projectId: string,
  to: ProjectStatus,
  opts: { override?: boolean; reason?: string } = {}
): Promise<{ project: Project; checks: GateCheck[]; overridden: boolean }> {
  return adminFetch(`/admin/projects/${encodeURIComponent(projectId)}/transition`, {
    method: "POST",
    body: JSON.stringify({ to, ...opts }),
  });
}

export async function setProjectBlocked(
  projectId: string,
  blocked: boolean,
  reason?: string
): Promise<Project> {
  const body: { blocked: boolean; reason?: string } = { blocked };
  const trimmed = reason?.trim();
  if (trimmed) body.reason = trimmed;
  const data = await adminFetch<{ project: Project }>(
    `/admin/projects/${encodeURIComponent(projectId)}/blocked`,
    { method: "POST", body: JSON.stringify(body) }
  );
  return data.project;
}

export interface ProjectJiraIssueRow {
  key: string;
  summary: string;
  status: string;
  statusCategory: string;
  assignee: string | null;
  assigneeAccountId: string | null;
  priority: string | null;
  updated: string;
  flagged: boolean;
  url: string;
  /**
   * Every scope name that matched this issue (D1-a). An issue claimed by two
   * scopes is ONE row carrying both names — never two rows.
   */
  workStreams: string[];
  /** JSM Organization / Account — customer the ticket is for (Support desk). */
  organization?: string | null;
}

export interface ProjectJiraRollups {
  total: number;
  done: number;
  open: number;
  blocked: number;
}

export interface ProjectJiraIssuesResponse {
  projectId: string;
  /** First scope — the single-scope back-compat contract. */
  scope: JiraScope | null;
  jql: string | null;
  /** Every named scope the project claims; rows are the deduped union. */
  scopes: NamedScope[];
  jqls: string[];
  rollups: ProjectJiraRollups;
  issues: ProjectJiraIssueRow[];
  /**
   * A scope ran past the server's page cap, so `issues`/`rollups` are partial.
   * A whole-board scope (support → TICKET) does this. Surface it rather than
   * presenting a truncated count as the truth.
   */
  truncated?: boolean;
}

export async function getProjectJiraIssues(projectId: string): Promise<ProjectJiraIssuesResponse> {
  return adminFetch<ProjectJiraIssuesResponse>(
    `/admin/projects/${encodeURIComponent(projectId)}/jira-issues`
  );
}

/** Overview mission control + Active today (WP-B). */
export interface NowNextBlocked {
  now: string;
  next: string;
  blocked: string;
}

export interface ProjectHeartbeatFocus {
  id: string;
  text: string;
  done: boolean;
  issueKey?: string;
  personUserId: string;
  displayName: string;
  personSlug?: string;
  avatarKey?: string;
}

export interface ProjectHeartbeatCheckout {
  holder: string;
  displayName: string;
  personSlug?: string;
  avatarKey?: string;
  startedAt: number;
  note?: string;
  state: string;
}

export interface StandupMentionRef {
  date: string;
  summary: string;
  sectionId: string | null;
  transcriptAvailable: boolean;
}

export interface ProjectHeartbeatResponse {
  projectId: string;
  date: string;
  nowNextBlocked: NowNextBlocked;
  progress: { done: number; inProgress: number; todo: number; total: number };
  weeklyTrend: { doneThisWeek: number; donePrevWeek: number; delta: number };
  rollups: ProjectJiraRollups;
  issues: ProjectJiraIssueRow[];
  activeToday: {
    focuses: ProjectHeartbeatFocus[];
    checkouts: ProjectHeartbeatCheckout[];
    lastStandupMention: StandupMentionRef | null;
  };
  mentionedInStandup: StandupMentionRef | null;
  mentionEvents: Array<{
    id: string;
    projectId: string;
    eventType: "standup-mention";
    nzDate: string;
    text: string;
    sectionId?: string;
    createdAt: number;
  }>;
  summary: ProjectSummary | null;
}

export async function getProjectHeartbeat(projectId: string): Promise<ProjectHeartbeatResponse> {
  return adminFetch<ProjectHeartbeatResponse>(
    `/admin/projects/${encodeURIComponent(projectId)}/heartbeat`
  );
}

/** Project Activity feed (WP-B3) — Overview Recent + Activity tab. */
export type ProjectEventType = "jira" | "session" | "note" | "decision" | "standup" | "doc";

export type ProjectEvent = {
  at: string;
  actor: string;
  actorKind: "user" | "agent" | "system";
  type: ProjectEventType;
  text: string;
  objectKey?: string;
  objectUrl?: string;
};

export async function getProjectEvents(
  projectId: string,
  opts: { since?: string; types?: string | ProjectEventType[] } = {}
): Promise<{ events: ProjectEvent[]; lastVisit?: string }> {
  const q = new URLSearchParams();
  if (opts.since) q.set("since", opts.since);
  if (opts.types) {
    q.set("types", Array.isArray(opts.types) ? opts.types.join(",") : opts.types);
  }
  const qs = q.toString();
  return adminFetch<{ events: ProjectEvent[]; lastVisit?: string }>(
    `/admin/projects/${encodeURIComponent(projectId)}/events${qs ? `?${qs}` : ""}`
  );
}

export interface TranscriptSection {
  id: string;
  label: string;
  startOffset: number;
  startLine: number;
}

export interface JiraIssueDetail {
  key: string;
  summary: string;
  status: string;
  statusCategory: string;
  assignee: string | null;
  description: string;
  checklist: { text: string; checked: boolean }[];
  commentCount: number;
  url: string;
  updated: string;
}

export async function getJiraIssueDetail(key: string): Promise<JiraIssueDetail> {
  return adminFetch<JiraIssueDetail>(`/admin/jira/issues/${encodeURIComponent(key)}`);
}

/** Accept a Suggested issue → create Jira issue (P4). */
export async function pushSuggestedIssue(input: {
  actionId: string;
  projectId: string;
  summary: string;
  description?: string;
  issueType?: string;
}): Promise<{ key: string; url: string }> {
  return adminFetch<{ key: string; url: string }>("/admin/projects/suggested-issues/push", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

/** Orphan Jira issues (no matching brain scope) + assign via ws-* label (P3). */
export async function getOrphanJiraIssues(): Promise<{
  issues: ProjectJiraIssueRow[];
}> {
  return adminFetch(`/admin/jira/orphans`);
}

export async function assignOrphanToProject(input: {
  key: string;
  projectId: string;
}): Promise<{ ok: boolean; label: string }> {
  return adminFetch(`/admin/jira/orphans/assign`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

/** Jira boards + epic picker for the scope editor. */
export interface JiraBoardRef {
  key: string;
  name: string;
  id: string;
}
export async function listJiraBoards(): Promise<{ boards: JiraBoardRef[] }> {
  return adminFetch(`/admin/jira/boards`);
}
export async function searchJiraEpics(
  board: string,
  query = ""
): Promise<{ epics: Array<{ key: string; summary: string; status: string }> }> {
  const q = new URLSearchParams({ board, query });
  return adminFetch(`/admin/jira/epics?${q}`);
}

/** Collector sweep — unlabeled work the project's epic/fixVersion nets caught. */
export interface CollectorCandidate {
  key: string;
  summary: string;
  status: string;
  statusCategory: string;
  assignee: string | null;
  updated: string;
  url: string;
  collector: string;
}
export async function getProjectCollectors(
  projectId: string
): Promise<{ label: string | null; candidates: CollectorCandidate[]; dismissed: string[] }> {
  return adminFetch(`/admin/projects/${encodeURIComponent(projectId)}/jira-collectors`);
}
export async function stampCollectorIssues(
  projectId: string,
  keys: string[]
): Promise<{ label: string; stamped: string[]; failed: Array<{ key: string; error: string }> }> {
  return adminFetch(`/admin/projects/${encodeURIComponent(projectId)}/jira-collectors/stamp`, {
    method: "POST",
    body: JSON.stringify({ keys }),
  });
}
export async function dismissCollectorIssue(
  projectId: string,
  key: string
): Promise<{ ok: boolean; dismissed: string[] }> {
  return adminFetch(`/admin/projects/${encodeURIComponent(projectId)}/jira-collectors/dismiss`, {
    method: "POST",
    body: JSON.stringify({ key }),
  });
}

/** Project catch-me-up since last visit (P5 / WP-A). */
export async function getProjectCatchUp(
  projectId: string,
  since?: string
): Promise<{
  since: string;
  daysAway?: number;
  digest?: string;
  items: { kind: string; text: string; at: string; actor?: string }[];
}> {
  const q = since ? `?since=${encodeURIComponent(since)}` : "";
  return adminFetch(`/admin/projects/${encodeURIComponent(projectId)}/catch-up${q}`);
}

export async function getStaleProjects(days = 7): Promise<{
  days: number;
  projects: { projectId: string; name: string; lastMovement?: string; status?: string }[];
}> {
  return adminFetch(`/admin/projects/stale?days=${days}`);
}

// --- Standup Projects: registry + capture loop (brain_* over MySQL/Aurora) ------

export interface BrainProject {
  projectId: string;
  name: string;
  customerId?: string;
  customerName?: string;
  companyId?: number;
  crmProjectReference?: string;
  vibestackProjectId?: number;
  status: string;
  description?: string;
  owner?: string;
  startedAt?: string;
  shippedAt?: string;
  deadlineDate?: string;
  percentComplete?: number;
  isFocus: boolean;
  jiraTarget?: string;
  workspaceDirs: string[];
  shipStatusDocs: string[];
  openActions?: number;
  source: string;
  meta: Record<string, unknown>;
  createdAt?: number;
  updatedAt?: number;
}

export interface BrainProjectCustomer {
  customerId: string;
  name: string;
  legalName?: string;
  status: string;
  crmCompanyId?: number;
  primaryContact?: string;
  domains: string[];
}

export interface ProjectNote {
  id: string;
  projectId: string;
  nzDate: string;
  author?: string;
  text: string;
  createdAt?: number;
}

export type ActionKind = "action" | "note" | "question";
export type ActionStatus = "proposed" | "captured" | "done" | "dismissed" | "suggested";
export type ClientStatus =
  | "With Aeronet"
  | "With Client"
  | "In Review"
  | "Approved"
  | "Awaiting Customer"
  | "Done"
  | "Blocked";

export interface ActionLink {
  type: string;
  ref: string;
}

export interface ProjectAction {
  id: string;
  projectId?: string;
  kind: ActionKind | "blocker";
  text: string;
  status: ActionStatus;
  assignee?: string;
  author?: string;
  dueDate?: string;
  sourceType?: string;
  sourceRef?: string;
  sourceName?: string;
  jiraIssueKey?: string;
  nzDate?: string;
  createdAt?: number;
  updatedAt?: number;
  workStream?: string;
  priority?: string;
  owedBy?: "aeronet" | "customer";
  clientStatus?: ClientStatus | string;
  contact?: string;
  isGate?: boolean;
  parentActionId?: string;
  customerRef?: string;
  raisedBy?: string;
  verifiedBy?: string;
  verifiedDate?: string;
  reminderDate?: string;
  links?: ActionLink[];
  chargeable?: boolean;
  targetType?: string;
  targetId?: string;
  // Call-report alignment (Jay, 2026-08-06 schema-gaps §1/§2).
  category?: ActionCategory;
  needsTicket?: boolean;
  raiseTarget?: string;
  /** Free context (CRM `comments`; absorbed the old `notes` field 2026-08-10). */
  comments?: string;
  // CRM schema version-12 parity (2026-08-10).
  releaseVersion?: string;
  /** Semicolon-separated labels (a;b;c) — CRM convention kept verbatim. */
  tags?: string;
  external?: boolean;
}

/** Call-report action taxonomy — mirrors the server's ACTION_CATEGORIES. */
export type ActionCategory = "fr" | "support" | "support-bug" | "support-fr";

export const ACTION_CATEGORY_LABELS: Record<ActionCategory, string> = {
  fr: "FR",
  support: "Support",
  "support-bug": "Support (bug)",
  "support-fr": "Support/FR?",
};

/** Where a needed ticket gets raised — mirrors the server RAISE_TARGETS keys. */
export const RAISE_TARGET_LABELS: Record<string, string> = {
  dev: "L3·AN (dev)",
  support: "L2·AS (support)",
  "feature-request": "FR (feature request)",
};

export interface ProjectFollowup {
  id: string;
  projectId?: string;
  customerId?: string;
  text: string;
  status: "open" | "done" | "dropped" | "superseded";
  origin?: string;
  createdAt?: number;
  resolvedAt?: number;
  resolution?: string;
}

export interface WorkLogRow {
  id: string;
  customerId: string;
  person: string;
  date: string;
  hours: number;
  description: string;
  billable?: boolean;
  month?: string;
}

export interface CustomerEngagement {
  workStreams: string[];
  anchorProjectId: string | null;
  actions: ProjectAction[];
}

export interface ProjectSummary {
  projectId: string;
  nzDate: string;
  summaryText?: string;
  claims: { text: string; source?: { type: string; ref: string } }[];
  editedText?: string;
  flaggedDown: boolean;
  asOf?: number;
  nowNextBlocked?: NowNextBlocked;
  sectionId?: string;
}

export interface ProjectDetail {
  project: BrainProject;
  notes?: ProjectNote[];
  actions?: ProjectAction[];
  summary?: ProjectSummary | null;
}

/** Registry projects. focusOnly → the team's starred standup focuses. */
export async function getAaronProjects(opts: { focusOnly?: boolean; company?: number; customer?: string } = {}): Promise<BrainProject[]> {
  const p = new URLSearchParams();
  if (opts.focusOnly) p.set("focus", "1");
  if (opts.company !== undefined) p.set("company", String(opts.company));
  if (opts.customer) p.set("customer", opts.customer);
  const q = p.toString();
  const data = await adminFetch<{ projects: BrainProject[] }>(`/admin/aaron-projects${q ? `?${q}` : ""}`);
  return data.projects;
}

/** Project detail + (when date given) its notes, actions, and AI summary. */
export async function getAaronProject(projectId: string, date?: string): Promise<ProjectDetail> {
  const q = date ? `?date=${encodeURIComponent(date)}` : "";
  return adminFetch<ProjectDetail>(`/admin/aaron-projects/${encodeURIComponent(projectId)}${q}`);
}

export async function setProjectFocus(projectId: string, isFocus: boolean): Promise<void> {
  await adminFetch(`/admin/aaron-projects/${encodeURIComponent(projectId)}/focus`, { method: "POST", body: JSON.stringify({ isFocus }) });
}

export async function listAaronCustomers(): Promise<BrainProjectCustomer[]> {
  const data = await adminFetch<{ customers: BrainProjectCustomer[] }>("/admin/aaron-customers");
  return data.customers;
}

export async function addProjectNote(projectId: string, input: { date: string; text: string; author?: string }): Promise<ProjectNote> {
  const data = await adminFetch<{ note: ProjectNote }>(`/admin/aaron-projects/${encodeURIComponent(projectId)}/notes`, { method: "POST", body: JSON.stringify(input) });
  return data.note;
}

export async function deleteProjectNote(projectId: string, noteId: string): Promise<void> {
  await adminFetch(`/admin/aaron-projects/${encodeURIComponent(projectId)}/notes/${encodeURIComponent(noteId)}`, { method: "DELETE" });
}

export async function listProjectActions(opts: { project?: string; status?: ActionStatus; date?: string; general?: boolean } = {}): Promise<ProjectAction[]> {
  const p = new URLSearchParams();
  if (opts.project) p.set("project", opts.project);
  if (opts.status) p.set("status", opts.status);
  if (opts.date) p.set("date", opts.date);
  if (opts.general) p.set("general", "1");
  const q = p.toString();
  const data = await adminFetch<{ actions: ProjectAction[] }>(`/admin/aaron-actions${q ? `?${q}` : ""}`);
  return data.actions;
}

type ActionCreateFields = Partial<Pick<
  ProjectAction,
  | "dueDate"
  | "sourceType"
  | "sourceRef"
  | "sourceName"
  | "jiraIssueKey"
  | "nzDate"
  | "workStream"
  | "priority"
  | "owedBy"
  | "clientStatus"
  | "contact"
  | "isGate"
  | "parentActionId"
  | "customerRef"
  | "raisedBy"
  | "verifiedBy"
  | "verifiedDate"
  | "reminderDate"
  | "links"
  | "chargeable"
  | "targetType"
  | "targetId"
  | "category"
  | "needsTicket"
  | "raiseTarget"
  | "comments"
  | "releaseVersion"
  | "tags"
  | "external"
>>;

export type ActionCreateInput = ActionCreateFields & {
  projectId?: string;
  kind: "action";
  text: string;
  status?: ActionStatus;
  assignee?: string;
};

export type NoteQuestionCreateInput = {
  projectId: string;
  kind: "note" | "question";
  text: string;
  assignee?: string;
};

export type CreateProjectActionInput = ActionCreateInput | NoteQuestionCreateInput;

export async function createProjectAction(input: CreateProjectActionInput): Promise<ProjectAction> {
  const data = await adminFetch<{ action: ProjectAction }>("/admin/aaron-actions", { method: "POST", body: JSON.stringify(input) });
  return data.action;
}

export type UpdateProjectActionPatch = Partial<
  Pick<
    ProjectAction,
    | "status"
    | "dueDate"
    | "text"
    | "projectId"
    | "jiraIssueKey"
    | "isGate"
    | "parentActionId"
    | "sourceName"
    | "raisedBy"
    | "verifiedBy"
    | "verifiedDate"
    | "reminderDate"
    | "links"
    | "chargeable"
    | "targetType"
    | "targetId"
  >
> & {
  // Explicit null clears a field server-side (undefined keys are dropped by JSON.stringify).
  workStream?: string | null;
  priority?: string | null;
  owedBy?: "aeronet" | "customer" | null;
  clientStatus?: ClientStatus | string | null;
  contact?: string | null;
  assignee?: string | null;
  customerRef?: string | null;
  category?: ActionCategory | null;
  needsTicket?: boolean | null;
  raiseTarget?: string | null;
  comments?: string | null;
  releaseVersion?: string | null;
  tags?: string | null;
  external?: boolean | null;
  // Verify intent — the server stamps verifiedBy/verifiedDate from the session.
  verify?: boolean;
};

export async function updateProjectAction(
  id: string,
  patch: UpdateProjectActionPatch,
): Promise<ProjectAction> {
  const data = await adminFetch<{ action: ProjectAction }>(`/admin/aaron-actions/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(patch) });
  return data.action;
}

export interface ActionHistoryEntry {
  actionId: string;
  projectId?: string;
  actor: string;
  actorKind: "user" | "agent";
  ts: number;
  changes: { field: string; from: string | null; to: string | null }[];
}

export async function listActionHistory(actionId: string, limit = 50): Promise<ActionHistoryEntry[]> {
  const data = await adminFetch<{ history: ActionHistoryEntry[] }>(
    `/admin/aaron-actions/${encodeURIComponent(actionId)}/history?limit=${limit}`,
  );
  return data.history;
}

export async function escalateActionToJira(
  actionId: string,
  body: { target?: string; issueType?: string; summary?: string; description?: string; priority?: string } = {},
): Promise<{ key: string; url: string; action: ProjectAction | null; warning?: string }> {
  return adminFetch(`/admin/aaron-actions/${encodeURIComponent(actionId)}/escalate`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function listFollowups(opts: {
  project?: string;
  customer?: string;
  status?: string;
  general?: boolean;
} = {}): Promise<ProjectFollowup[]> {
  const p = new URLSearchParams();
  if (opts.project) p.set("project", opts.project);
  if (opts.customer) p.set("customer", opts.customer);
  if (opts.status) p.set("status", opts.status);
  if (opts.general) p.set("general", "1");
  const q = p.toString();
  const data = await adminFetch<{ followups: ProjectFollowup[] }>(`/admin/aaron-followups${q ? `?${q}` : ""}`);
  return data.followups;
}

export async function createFollowupApi(input: {
  text: string;
  projectId?: string;
  customerId?: string;
}): Promise<ProjectFollowup> {
  const data = await adminFetch<{ followup: ProjectFollowup }>("/admin/aaron-followups", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return data.followup;
}

export async function resolveFollowupApi(
  id: string,
  outcome: "done" | "dropped" | "superseded",
  resolution?: string,
): Promise<ProjectFollowup> {
  const data = await adminFetch<{ followup: ProjectFollowup }>(
    `/admin/aaron-followups/${encodeURIComponent(id)}`,
    { method: "PATCH", body: JSON.stringify({ outcome, resolution }) },
  );
  return data.followup;
}

export async function createProjectActivity(input: {
  projectId: string;
  text: string;
  type?: string;
  date?: string;
  customerId?: string;
  contact?: string;
  actor?: string;
  sourceRef?: string;
}): Promise<unknown> {
  const data = await adminFetch<{ activity: unknown }>("/admin/aaron-activity", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return data.activity;
}

export async function listWorklogs(opts: { customer: string; month?: string }): Promise<WorkLogRow[]> {
  const p = new URLSearchParams({ customer: opts.customer });
  if (opts.month) p.set("month", opts.month);
  const data = await adminFetch<{ worklogs: WorkLogRow[] }>(`/admin/aaron-worklogs?${p}`);
  return data.worklogs;
}

export async function getCustomerEngagement(customerId: string): Promise<CustomerEngagement> {
  const data = await adminFetch<{ engagement: CustomerEngagement }>(
    `/admin/aaron-engagement/${encodeURIComponent(customerId)}`,
  );
  return data.engagement;
}

// --- Company skills registry (Dave's mod-skills → Bedrock AgentCore + S3 mirror) ---

export interface CompanySkill {
  name: string;
  description: string | null;
  category: string | null;
  files: string[];
  syncedAt: string | null;
  gitSha: string | null;
}

export async function listCompanySkills(): Promise<CompanySkill[]> {
  const data = await adminFetch<{ skills: CompanySkill[] }>("/admin/company-skills");
  return data.skills;
}

export async function getCompanySkillFile(name: string, file = "SKILL.md"): Promise<{ name: string; file: string; content: string }> {
  return adminFetch(`/admin/company-skills/${encodeURIComponent(name)}?file=${encodeURIComponent(file)}`);
}

/** One thought by vector key — backs the chat widget's thought: chips. */
export interface ThoughtDetail {
  id: string;
  scope: string;
  content: string;
  type?: string;
  topics?: string[];
  people?: string[];
  created_at?: number;
  display_name?: string;
  status?: string;
}

export async function getThought(id: string): Promise<ThoughtDetail> {
  const data = await adminFetch<{ thought: ThoughtDetail }>(`/admin/thoughts/${encodeURIComponent(id)}`);
  return data.thought;
}

export async function getBoard(opts: { customer?: string; priority?: string; assignee?: string; status?: string } = {}): Promise<{
  rows: {
    projectId: string;
    name: string;
    customer?: string;
    status: string;
    priority?: string;
    assignee?: string;
    reporter?: string;
    nextAction?: string;
    kind: string;
  }[];
  count: number;
}> {
  const p = new URLSearchParams();
  if (opts.customer) p.set("customer", opts.customer);
  if (opts.priority) p.set("priority", opts.priority);
  if (opts.assignee) p.set("assignee", opts.assignee);
  if (opts.status) p.set("status", opts.status);
  const q = p.toString();
  return adminFetch(`/admin/aaron-board${q ? `?${q}` : ""}`);
}

/** Drift sweep — blocked / stale / unassigned work across every open project. */
export type DriftReason = "blocked" | "stale" | "unassigned";

export interface DriftRow {
  source: "issue" | "action";
  ref: string;
  title: string;
  projectId: string;
  projectName: string;
  status: string;
  assignee: string | null;
  priority: string | null;
  url: string | null;
  updated: string | null;
  /** Null when the row carries no usable timestamp — render as `—`, not `0d`. */
  idleDays: number | null;
  /** A row can be all three at once; the buckets deliberately overlap. */
  reasons: DriftReason[];
}

export interface DriftResult {
  staleDays: number;
  generatedAt: number;
  counts: { blocked: number; stale: number; unassigned: number; rows: number };
  rows: DriftRow[];
}

export async function getDrift(staleDays?: number): Promise<DriftResult> {
  const q = staleDays ? `?staleDays=${encodeURIComponent(String(staleDays))}` : "";
  return adminFetch<DriftResult>(`/admin/aaron-drift${q}`);
}

export type ActivityType =
  | "note"
  | "correspondence"
  | "question"
  | "decision"
  | "meeting"
  | "deploy"
  | "scope_change"
  | "standup";

export interface ProjectActivityRow {
  id: string;
  projectId: string;
  customerId?: string;
  nzDate: string;
  type: ActivityType | string;
  actor?: string;
  contact?: string;
  text: string;
  createdAt?: number;
}

export async function listProjectTypedActivity(projectId: string, limit = 100): Promise<ProjectActivityRow[]> {
  const p = new URLSearchParams({ project: projectId, limit: String(limit) });
  const data = await adminFetch<{ activity: ProjectActivityRow[] }>(`/admin/aaron-activity?${p}`);
  return data.activity;
}

export async function patchProjectSummary(projectId: string, input: { date: string; editedText?: string; flaggedDown?: boolean }): Promise<ProjectSummary | null> {
  const data = await adminFetch<{ summary: ProjectSummary | null }>(`/admin/aaron-projects/${encodeURIComponent(projectId)}/summary`, { method: "PATCH", body: JSON.stringify(input) });
  return data.summary;
}

// --- Current-version hero (the always-on release card) --------------------------

export interface ReleaseTicket {
  key: string;
  summary: string;
  status: string;
  statusCategory: string; // "new" | "indeterminate" | "done"
  priority?: string;
  url: string;
}

export interface ReleaseStatus {
  fixVersion: string;
  displayVersion: string;
  configured: boolean; // false when Jira isn't reachable / configured
  total: number;
  done: number;
  inProgress: number;
  toDo: number;
  percent: number;
  blocked: ReleaseTicket[];
  topPriority: ReleaseTicket[];
  /**
   * The release card's four-segment bar (handoff §5.1). MUTUALLY EXCLUSIVE and
   * sums to `total` — unlike done/inProgress/toDo + blocked, which overlap.
   * Optional: the deployed API predates it, so the card falls back rather than
   * drawing a bar that does not add up.
   */
  segments?: { done: number; inProgress: number; blocked: number; notStarted: number };
  /**
   * Dated milestones from the Jira version itself (handoff §5.1). Optional: the
   * deployed API predates it. Empty means Jira holds no dates for this release —
   * the card says so rather than drawing a schedule nobody entered.
   */
  milestones?: ReleaseMilestone[];
  fetchedAt: number;
}

/** A dated point on the release train. `date` is YYYY-MM-DD. */
export interface ReleaseMilestone {
  key: "start" | "release";
  label: string;
  date: string;
}

export interface VersionDetail {
  project: BrainProject | null; // null until the version is configured
  release: ReleaseStatus | null;
  actions: ProjectAction[]; // blockers (kind:"blocker") + next actions (kind:"action")
}

/** The reserved "current version" project: live Jira release status + its manual lists. */
export async function getAaronVersion(): Promise<VersionDetail> {
  return adminFetch<VersionDetail>("/admin/aaron-version");
}

/** Set the displayed version + the Jira fixVersion it aggregates (admin). */
export async function patchAaronVersion(input: { displayVersion: string; jiraFixVersion: string }): Promise<{ project: BrainProject | null }> {
  return adminFetch<{ project: BrainProject | null }>("/admin/aaron-version", { method: "PATCH", body: JSON.stringify(input) });
}

/** Live epic-map synthesis for the current release (heroes, ticket coverage). */
export interface ReleaseSynthesisHero {
  epicKey: string;
  epicName: string;
  issues: number;
  statusMix: Record<string, number>;
  assignees: Record<string, number>;
  chargeable: number;
  passengerTickets: Array<{ key: string; summary: string }>;
  url: string;
}

export interface ReleaseSynthesis {
  version: string;
  configured: boolean;
  fetchedAt: number;
  kpis: {
    devIssues: number;
    epics: number;
    featureIssues: number;
    featureDone: number;
    maintenanceIssues: number;
    maintenanceDone: number;
    tickets: number;
    ticketsLinked: number;
    ticketsUnlinked: number;
    chargeable: number;
    chargeablePct: number;
  };
  heroes: ReleaseSynthesisHero[];
  /** Every epic in the release with all of its issues — the card's drill-down.
   *  Optional: absent from the deployed API until this ships. */
  epicGroups?: ReleaseEpicGroup[];
  unlinkedTickets: Array<{ key: string; summary: string; status: string }>;
  findings: string[];
}

/** An epic as the release card draws it: mono key, name, module, done/total. */
export interface ReleaseEpicGroup {
  epicKey: string;
  epicName: string;
  module: string;
  done: number;
  blocked: number;
  total: number;
  url: string;
  issues: Array<{
    key: string;
    summary: string;
    status: string;
    statusCategory: string;
    priority: string | null;
    assignee: string | null;
    blocked: boolean;
    maintenance: boolean;
    chargeable: boolean;
    url: string;
  }>;
}

export async function getAaronVersionSynthesis(): Promise<{
  project: BrainProject | null;
  projectId: string;
  synthesis: ReleaseSynthesis | null;
  actions: ProjectAction[];
}> {
  return adminFetch("/admin/aaron-version/synthesis");
}

// --- Customer relational spine (Goal 2) — companies + projects from aaron-stack -

export interface CustomerCompany {
  id: number;
  name?: string;
  companyType?: string;
  type?: string;
  category?: string;
  status?: string;
  active?: boolean;
  email?: string;
  city?: string;
  country?: string;
  aeronetAccountCode?: string;
}

export interface CustomerSystem {
  id: number;
  domain?: string;
  status?: string;
  server?: string;
  distVersion?: string;
  roxenVersion?: string;
  release?: string;
  aircraftCount?: number;
  userCount?: number;
  backupDate?: string;
}

export interface CustomerSpine {
  companyId: number | null;
  linked: boolean;
  company: CustomerCompany | null;
  projects: BrainProject[];
  systems: CustomerSystem[];
  systemsMappingPending: boolean;
  subscription: { plan: null; seats: null; mrr: null; renewalDate: null };
}

export async function getCustomerSpine(customerId: string): Promise<CustomerSpine> {
  const data = await adminFetch<{ spine: CustomerSpine }>(`/admin/customers/${encodeURIComponent(customerId)}/spine`);
  return data.spine;
}

/** The atom stream for a department (most-recent first), via the team facet + optional types. */
export async function deptActivity(team: string, types?: string[], limit = 30): Promise<Thought[]> {
  const result = await callTool("browse_recent", {
    scope: "all",
    limit,
    filters: { team, ...(types && types.length ? { type: types } : {}) },
    _format: "json",
  });
  return parseJson<{ thoughts: Thought[] }>("browse_recent", result).thoughts;
}

/** The atom stream for one project (most-recent first), via the project facet. */
export async function projectActivity(projectId: string, limit = 50): Promise<Thought[]> {
  const result = await callTool("browse_recent", {
    scope: "all",
    limit,
    filters: { project: [projectId] },
    _format: "json",
  });
  return parseJson<{ thoughts: Thought[] }>("browse_recent", result).thoughts;
}

/** The atom stream for one customer (most-recent first), via the customer facet. */
export async function customerActivity(customerId: string, limit = 50): Promise<Thought[]> {
  const result = await callTool("browse_recent", {
    scope: "all",
    limit,
    filters: { customer: [customerId] },
    _format: "json",
  });
  return parseJson<{ thoughts: Thought[] }>("browse_recent", result).thoughts;
}

/** The compiled Customer Brief wiki page (wiki:customer-<id>), if one exists. */
export async function getCustomerBrief(customerId: string): Promise<WikiPage | null> {
  const pages = await getWikiPages();
  return pages.find((p) => p.slug === `customer-${customerId}`) ?? null;
}

function parseJson<T>(toolName: string, text: string): T {
  if (text.startsWith("Error:")) {
    throw new Error(`${toolName}: ${text}`);
  }
  try {
    return JSON.parse(text) as T;
  } catch {
    throw new Error(`${toolName}: unexpected response format`);
  }
}

export async function searchThoughts(
  query: string,
  filters?: { type?: string; topic?: string; scope?: ReadScope; limit?: number }
): Promise<Thought[]> {
  const result = await callTool("search_thoughts", {
    query,
    scope: "all",
    limit: 20,
    ...filters,
    _format: "json",
  });
  return parseJson<{ thoughts: Thought[] }>("search_thoughts", result).thoughts;
}

export async function browseRecent(
  filters?: { type?: string; topic?: string; scope?: ReadScope; limit?: number; human_only?: boolean }
): Promise<Thought[]> {
  const result = await callTool("browse_recent", {
    scope: "all",
    limit: 20,
    ...filters,
    _format: "json",
  });
  return parseJson<{ thoughts: Thought[] }>("browse_recent", result).thoughts;
}

export interface WikiPage {
  docId: string;
  slug: string;
  scope: AnyScope;
  compiledAt: number | null;
  markdown: string;
}

/**
 * Phase-9 living wiki: fetch compiled brief pages, reconstructing each page by
 * grouping its chunks (doc_id "wiki:{slug}") and joining them in chunk order.
 */
export async function getWikiPages(): Promise<WikiPage[]> {
  const result = await callTool("browse_recent", {
    scope: "all",
    limit: 500,
    filters: { source: "wiki" },
    _format: "json",
  });
  const { thoughts } = parseJson<{ thoughts: Thought[] }>("browse_recent", result);

  const byDoc = new Map<string, Thought[]>();
  for (const t of thoughts) {
    if (!t.doc_id) continue;
    (byDoc.get(t.doc_id) ?? byDoc.set(t.doc_id, []).get(t.doc_id)!).push(t);
  }

  const pages: WikiPage[] = [];
  for (const [docId, chunks] of byDoc) {
    chunks.sort((a, b) => ((a as Thought & { chunk?: number }).chunk ?? 0) - ((b as Thought & { chunk?: number }).chunk ?? 0));
    pages.push({
      docId,
      slug: docId.replace(/^wiki:/, ""),
      scope: chunks[0]?.scope ?? "company",
      compiledAt: chunks[0]?.created_at ?? null,
      markdown: chunks.map((c) => c.content).join("\n\n"),
    });
  }
  return pages.sort((a, b) => (b.compiledAt ?? 0) - (a.compiledAt ?? 0));
}

/**
 * Fetch the standup Meet transcript for a past day via the first-class
 * `get_standup_transcript` tool (reassembles `standup-transcript:{date}` chunks).
 * Returns null when no transcript was ingested for that date.
 * Includes section anchors (WP-B) for deep-linking into the transcript modal.
 */
export async function getStandupTranscript(
  date: string
): Promise<{ transcript: string; sections: TranscriptSection[] } | null> {
  try {
    const result = await callTool("get_standup_transcript", {
      date,
      _format: "json",
    });
    const parsed = parseJson<{
      date: string;
      transcript: string | null;
      sections?: TranscriptSection[];
    }>("get_standup_transcript", result);
    const text = typeof parsed.transcript === "string" ? parsed.transcript.trim() : "";
    if (!text) return null;
    return { transcript: text, sections: Array.isArray(parsed.sections) ? parsed.sections : [] };
  } catch {
    return null;
  }
}

export async function getStats(): Promise<BrainStats> {
  const result = await callTool("stats", { _format: "json" });
  return parseJson<BrainStats>("stats", result);
}

export async function getBusActivity(params?: {
  hours?: number;
  agent?: string;
  limit?: number;
}): Promise<BusActivity> {
  const result = await callTool("bus_activity", {
    ...params,
    _format: "json",
  });
  return parseJson<BusActivity>("bus_activity", result);
}

export async function listAgents(): Promise<Agent[]> {
  const result = await callTool("list_agents", { _format: "json" });
  return parseJson<{ agents: Agent[] }>("list_agents", result).agents;
}

export async function createAgent(name: string): Promise<string> {
  return callTool("create_agent", { name });
}

export async function revokeAgent(name: string): Promise<string> {
  return callTool("revoke_agent", { name });
}

export async function rotateAgentKey(name: string): Promise<string> {
  return callTool("rotate_agent_key", { name });
}

function extractUrl(text: string): string | undefined {
  try {
    const match = text.match(/https?:\/\/[^\s]+/);
    if (!match) return undefined;
    const urlText = match[0].replace(/[)\]>"'.,;:!?]+$/u, "");
    new URL(urlText);
    return urlText;
  } catch {
    return undefined;
  }
}

export async function captureThought(
  text: string,
  scope: Scope = "personal",
  type?: string
): Promise<string> {
  const sourceUrl = extractUrl(text);
  return callTool("capture_thought", {
    text,
    scope,
    ...(sourceUrl ? { source_url: sourceUrl } : {}),
    ...(type && type !== "auto" ? { type } : {}),
  });
}

export async function updateThought(
  id: string,
  text: string,
  scope: AnyScope = "personal"
): Promise<string> {
  return callTool("update_thought", { id, text, scope });
}

export async function deleteThought(
  id: string,
  scope: AnyScope = "personal"
): Promise<string> {
  return callTool("delete_thought", { id, scope });
}

export async function morningBrief(): Promise<string> {
  return callTool("morning_brief", { _format: "markdown" });
}

// --- Review flow (Phase 3) ---

export async function reviewPending(scope?: string, limit = 50): Promise<PendingItem[]> {
  const result = await callTool("review_pending", {
    ...(scope ? { scope } : {}),
    limit,
    _format: "json",
  });
  return parseJson<{ pending: PendingItem[] }>("review_pending", result).pending;
}

export async function approveMemory(itemId: string, targetScope: string): Promise<string> {
  return callTool("approve_memory", { item_id: itemId, target_scope: targetScope });
}

export async function rejectMemory(
  itemId: string,
  targetScope: string,
  reason?: string
): Promise<string> {
  return callTool("reject_memory", {
    item_id: itemId,
    target_scope: targetScope,
    ...(reason ? { reason } : {}),
  });
}

// --- Finance (docs/design/finance-tab-plan.md) -------------------------------
// Server-gated: admin role OR finance department. Everyone else gets a 403.

export interface FinanceMonthCost {
  /** Resolved code (HELISUPPORT, SHARED, MOD, UNRESOLVED, __TOTAL__). */
  code: string;
  /** YYYY-MM. */
  month: string;
  /** DIRECTLY attributed cost — excludes any allocated shared spend. */
  cost: number;
  byService: Record<string, number>;
  /** Daily direct costs. Populated for EVERY month since the CUR rebuild. */
  days?: Record<string, number>;
  /** This code's share of the SHARED pool for the month (customers only). */
  allocated?: number;
  /** Daily allocated shared cost. */
  daysAllocated?: Record<string, number>;
  /** __TOTAL__ rows only — coverage is over attributable spend. */
  coverage?: { resolved: number; unresolved: number };
  /** __TOTAL__ rows only: SHARED cost with no customer footprint to land on. */
  unallocated?: number;
  /** __TOTAL__ rows only: tax + Savings Plan fees & credits + support. */
  nonAttributable?: number;
  updatedAt: number;
}

export interface FinanceRun { job: string; ts: number; ok: boolean; summary: Record<string, unknown> }

/** Pricing/settings block on the customer record (edited via the Finance tab). */
export interface CustomerFinanceSettings {
  /** Manual sizing bucket (small | medium | large). */
  dataTier?: string;
  /** Provenance of dataTier — see handleFinanceSettings `suggested` flag. */
  tierSource?: "suggested" | "confirmed";
  /** @deprecated Legacy — ignored by Review UI. */
  serverBand?: string;
  /** @deprecated Legacy — ignored by Review UI. */
  dataPlan?: string;
  /** @deprecated Legacy band provenance. */
  bandSource?: "suggested" | "confirmed";
  subscriptionMonthly?: number;
  currency?: string;
  reviewedAt?: string;
  reviewedBy?: string;
  notes?: string;
  pricing?: CustomerPricing;
}

export type PricingSection = "subscription" | "service" | "infrastructure";

/** One line on a customer's pricing summary sheet. */
export interface PricingLine {
  key?: string;
  section: PricingSection;
  label: string;
  amount: number;
  /** "per month" (default) | "per day" | "one-off". */
  unit?: string;
  qty?: number;
  /** Changed at this review — rendered in red on the sheet. */
  changed?: boolean;
}

export interface CustomerPricing {
  lines: PricingLine[];
  discountMonthly?: number;
  discountNote?: string;
  note?: string;
  updatedAt?: string;
  updatedBy?: string;
}

/** Standard rate-card item (Aeronet Standard Pricing). */
export interface PricingCatalogItem {
  key: string;
  section: PricingSection;
  label: string;
  amount: number;
  unit: "per month" | "per day" | "one-off";
  note?: string;
}

export interface PricingCatalog {
  currency: string;
  year: number;
  items: PricingCatalogItem[];
  updatedAt?: number;
  updatedBy?: string;
}

/** Install summary for the pricing sheet's CURRENT INSTALLS table. */
export interface FinanceInstallSite {
  alias?: string;
  appUrl?: string;
  release?: string;
  aircraft?: number;
  activeUsers?: number;
  lastLogin?: string;
}

/** Data-usage snapshot: backup tar size (history to 2016) + EBS/RDS footprint. */
export interface FinanceMonthUsage {
  code: string;
  month: string;
  backupBytes?: number;
  ebsBytes?: number;
  rdsBytes?: number;
  instanceTypes?: string[];
  updatedAt: number;
}

export interface FinanceAuditChange {
  kind: string;
  customerId: string;
  code: string;
  detail: string;
  before?: string | number;
  after?: string | number;
}

export interface FinanceAuditCustomer {
  customerId: string;
  name: string;
  code: string;
  suggestedServerBand: string;
  suggestedDataPlan?: string;
  setServerBand?: string;
  setDataPlan?: string;
  subscriptionMonthly?: number;
  backupBytes?: number;
  instanceTypes?: string[];
  lastFullMonthCost?: number;
  prevFullMonthCost?: number;
  flags: string[];
}

export interface FinanceAudit {
  date: string;
  customers: FinanceAuditCustomer[];
  orphanCodes: string[];
  changes: FinanceAuditChange[];
  updatedAt: number;
}

export interface FinanceOverview {
  rows: FinanceMonthCost[];
  codeMap: Record<string, { customerId: string; name: string; status: string; finance?: CustomerFinanceSettings }>;
  usage: Record<string, FinanceMonthUsage>;
  lastRun: FinanceRun | null;
}

export interface FinanceCustomerCosts {
  customerId: string;
  name?: string;
  product?: string[];
  codes: string[];
  rows: FinanceMonthCost[];
  usage: FinanceMonthUsage[];
  finance: CustomerFinanceSettings;
  installs?: FinanceInstallSite[];
}

export async function saveFinanceSettings(
  customerId: string,
  settings: {
    dataTier?: string;
    serverBand?: string;
    dataPlan?: string;
    subscriptionMonthly?: number | null;
    currency?: string;
    notes?: string;
    pricing?: CustomerPricing;
    /** When true with a tier/band write: mark suggested provenance and skip reviewed* stamp. */
    suggested?: boolean;
  }
): Promise<CustomerFinanceSettings> {
  const data = await adminFetch<{ ok: boolean; finance: CustomerFinanceSettings }>(
    `/finance/customer/${encodeURIComponent(customerId)}/settings`,
    { method: "POST", body: JSON.stringify(settings) }
  );
  return data.finance;
}

export async function getFinanceOverview(): Promise<FinanceOverview> {
  return adminFetch<FinanceOverview>("/finance/overview");
}

export async function getFinanceCustomer(customerId: string): Promise<FinanceCustomerCosts> {
  return adminFetch<FinanceCustomerCosts>(`/finance/customer/${encodeURIComponent(customerId)}`);
}

export async function startFinanceRefresh(): Promise<void> {
  await adminFetch<{ started: boolean }>("/finance/refresh", { method: "POST" });
}

export async function getFinanceStatus(): Promise<FinanceRun | null> {
  const data = await adminFetch<{ lastRun: FinanceRun | null }>("/finance/status");
  return data.lastRun;
}

export async function getFinanceAudits(limit = 6): Promise<{ audits: FinanceAudit[] }> {
  return adminFetch<{ audits: FinanceAudit[] }>(`/finance/audit?limit=${limit}`);
}

// --- resource inventory + attribution map (2026-07-27 CUR rebuild) -----------

/** Which resolver layer claimed a resource. `unresolved` is the audit backlog. */
export type AttributionSource = "override" | "tenancy" | "parent" | "rule" | "tag" | "alias" | "default" | "unresolved";

export interface FinanceResource {
  resourceId: string;
  service: string;
  region: string;
  /** USD over the inventory window. */
  cost: number;
  /** Raw Account tag from CUR, "" when absent. */
  tagCode: string;
  /** Resolved owner, or "MULTIPLE" when split across co-tenants. */
  code: string;
  weights?: Record<string, number>;
  source: AttributionSource;
}

export interface FinanceInventory {
  generatedAt: string | null;
  windowDays: number;
  total: number;
  totalCost: number;
  truncated: boolean;
  resources: FinanceResource[];
  owners: Record<string, { customerId: string; name: string }>;
}

export interface FinanceAttribution {
  generatedAt: string | null;
  bySource: Record<string, { cost: number; count: number }>;
  unresolved: FinanceResource[];
  overrides: number;
}

export interface FinanceAttributionRule {
  match: string;
  service?: string;
  code: string;
  order: number;
}

/** One resource on a customer's bill. `cost` is what the resource cost in total,
 *  `amount` is the slice that landed on this customer — the pair is what makes a
 *  shared line legible instead of arbitrary. */
export interface FinanceBreakdownLine {
  resourceId: string;
  /** EC2 Name tag / ENI description, when AWS knows one. */
  name?: string;
  service: string;
  region: string;
  cost: number;
  /** Fraction of `cost` charged to this customer. 1 for a sole-owner resource. */
  share: number;
  amount: number;
  source: AttributionSource;
  /** Why this layer claimed it — the rule pattern, the parent, the tag value. */
  detail?: string;
  /** Co-owners of a split resource. */
  weights?: Record<string, number>;
}

export interface FinanceCustomerBreakdown {
  customerId: string;
  codes: string[];
  generatedAt: string | null;
  /** Inclusive day bounds of the rolling window, YYYY-MM-DD. */
  from: string;
  to: string;
  days: number;
  direct: number;
  allocated: number;
  prevDirect: number;
  prevAllocated: number;
  /** Sum of the resource lines. A gap vs `direct` is cost the list can't explain. */
  directFromResources: number;
  sharedPool: number;
  resources: FinanceBreakdownLine[];
  shared: FinanceBreakdownLine[];
  regions: { region: string; pool: number; direct: number; share: number; amount: number }[];
  byService: Record<string, { direct: number; shared: number }>;
}

export async function getFinanceCustomerBreakdown(customerId: string): Promise<FinanceCustomerBreakdown> {
  return adminFetch<FinanceCustomerBreakdown>(`/finance/customer/${encodeURIComponent(customerId)}/breakdown`);
}

export async function getFinanceInventory(params: {
  code?: string;
  source?: string;
  minCost?: number;
  limit?: number;
} = {}): Promise<FinanceInventory> {
  const q = new URLSearchParams();
  if (params.code) q.set("code", params.code);
  if (params.source) q.set("source", params.source);
  if (params.minCost !== undefined) q.set("minCost", String(params.minCost));
  if (params.limit !== undefined) q.set("limit", String(params.limit));
  const qs = q.toString();
  return adminFetch<FinanceInventory>(`/finance/inventory${qs ? `?${qs}` : ""}`);
}

export async function getFinanceAttribution(): Promise<FinanceAttribution> {
  return adminFetch<FinanceAttribution>("/finance/attribution");
}

/** Map a resource to a customer (or an explicit split). Pass code "" to clear.
 *  The next pipeline run restates ALL history with the new mapping. */
export async function assignFinanceResource(body: {
  resourceId: string;
  code?: string;
  weights?: Record<string, number>;
  note?: string;
}): Promise<{ ok: boolean; error?: string; cleared?: boolean }> {
  return adminFetch<{ ok: boolean; error?: string; cleared?: boolean }>("/finance/inventory/assign", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function getFinanceRules(): Promise<{ rules: FinanceAttributionRule[] }> {
  return adminFetch<{ rules: FinanceAttributionRule[] }>("/finance/rules");
}

export async function saveFinanceRules(rules: FinanceAttributionRule[]): Promise<{ ok: boolean; error?: string }> {
  return adminFetch<{ ok: boolean; error?: string }>("/finance/rules", {
    method: "POST",
    body: JSON.stringify({ rules }),
  });
}

export async function startFinanceAudit(): Promise<void> {
  await adminFetch<{ started: boolean }>("/finance/audit", { method: "POST" });
}

export async function getFinanceCatalog(): Promise<{ catalog: PricingCatalog; source: "default" | "custom" }> {
  return adminFetch<{ catalog: PricingCatalog; source: "default" | "custom" }>("/finance/catalog");
}

export async function saveFinanceCatalog(catalog: { currency: string; year: number; items: PricingCatalogItem[] }): Promise<PricingCatalog> {
  const data = await adminFetch<{ ok: boolean; catalog: PricingCatalog }>("/finance/catalog", {
    method: "POST", body: JSON.stringify(catalog),
  });
  return data.catalog;
}


// ---------------------------------------------------------------------------
// Plan tab (docs/design/plan-tab) — /plan/* human-only REST
// ---------------------------------------------------------------------------

export type PlanThreadType = "feedback" | "question" | "comment";
export type PlanThreadStatus = "open" | "in_proposal" | "resolved" | "wontfix";
export type ProposalStatus = "open" | "changes_requested" | "signed_off" | "dismissed";

export interface PlanViewer {
  canSignOff: boolean;
  isOwner: boolean;
  isDeputy: boolean;
}

export interface PlanAuthor {
  userId: string;
  displayName: string;
  agentName?: string | null;
}

export interface PlanCitation {
  kind: "file" | "thought" | "ticket" | "url";
  ref: string;
  label?: string;
}

export interface PlanVersionEntry {
  versionId: string;
  signedOffBy: string;
  at: string;
  proposalId: string | null;
  note?: string;
  ordinal?: number;
  signOffs?: PlanSignOffEntry[];
}

export type PlanReviewStatus = "none" | "awaiting_review" | "changes_requested" | "approved";

export interface PlanSignOffEntry {
  versionId: string;
  by: string;
  userId: string;
  at: string;
  verdict: "approve" | "request_changes";
  comment?: string;
}

export interface PlanReviewer {
  personId: string;
  displayName: string;
  status: "pending" | "approved" | "changes_requested";
}

export interface PlanMeta {
  projectId: string;
  sk?: string;
  currentVersionId: string;
  versions: PlanVersionEntry[];
  createdAt: string;
  updatedAt: string;
  reviewStatus?: PlanReviewStatus;
  reviewers?: PlanReviewer[];
  reviewRequestedAt?: string;
  reviewRequestedBy?: string;
  signOffLedger?: PlanSignOffEntry[];
}

export interface PlanThreadMessage {
  id: string;
  author: PlanAuthor;
  body: string;
  citations?: PlanCitation[];
  ts: string;
}

export interface PlanThread {
  projectId: string;
  sk: string;
  type: PlanThreadType;
  status: PlanThreadStatus;
  target: {
    doc: "canonical" | "proposal";
    proposalId?: string | null;
    versionId: string;
    anchor?: { headingSlug?: string; quotedText?: string; docKey?: string } | null;
  };
  author: PlanAuthor;
  citations: PlanCitation[];
  messages: PlanThreadMessage[];
  resolution?: { by: string; reason: string; proposalId?: string | null; at: string };
  createdAt: string;
  updatedAt: string;
}

export interface PlanProposal {
  projectId: string;
  sk: string;
  summary: string;
  why?: string;
  author: PlanAuthor;
  baseVersionId: string;
  baseOrdinal: number;
  contentKey: string;
  status: ProposalStatus;
  addresses: string[];
  reviews: { reviewer: string; verdict: "approve" | "request_changes"; at: string; comment?: string }[];
  signedOff?: { by: string; at: string; planVersionId: string; planOrdinal: number; overriddenThreads?: string[] };
  dismissed?: { by: string; reason: string; at: string };
  /** Decision row ids carried by this proposal — decided at sign-off. */
  decisions?: string[];
  createdAt: string;
  updatedAt: string;
  stale?: boolean;
}

// --- Decisions (P3 §D) -------------------------------------------------------

export type DecisionStatus = "proposed" | "decided" | "superseded";

export interface Decision {
  projectId: string;
  sk: string;
  statement: string;
  why?: string;
  alternatives?: string;
  deciders: string[];
  status: DecisionStatus;
  supersededBy?: string;
  links: {
    planVersionId?: string;
    proposalId?: string;
    threadId?: string;
    jira?: string[];
    thoughts?: string[];
  };
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export async function listDecisions(projectId: string): Promise<Decision[]> {
  const data = await adminFetch<{ decisions: Decision[] }>(
    `/admin/projects/${encodeURIComponent(projectId)}/decisions`
  );
  return data.decisions;
}

export async function recordDecision(
  projectId: string,
  body: {
    statement: string;
    why?: string;
    alternatives?: string;
    deciders?: string[];
    links?: Decision["links"];
  }
): Promise<Decision> {
  const data = await adminFetch<{ decision: Decision }>(
    `/admin/projects/${encodeURIComponent(projectId)}/decisions`,
    { method: "POST", body: JSON.stringify(body) }
  );
  return data.decision;
}

// --- Project Jira tab (P4 §E2) ----------------------------------------------

export interface JiraTransitionOption {
  id: string;
  name: string;
  toStatus: string;
  /** Destination statusCategory.key when Jira returns it (`new` | `indeterminate` | `done`). */
  toStatusCategory?: string;
}

export async function getProjectJiraTransitions(
  projectId: string,
  key: string
): Promise<JiraTransitionOption[]> {
  const data = await adminFetch<{ transitions: JiraTransitionOption[] }>(
    `/admin/projects/${encodeURIComponent(projectId)}/jira/${encodeURIComponent(key)}/transitions`
  );
  return data.transitions;
}

export type ProjectJiraAction =
  | { action: "transition"; transitionId: string; toStatus?: string }
  | { action: "assign"; accountId: string | null; assigneeName?: string }
  | { action: "comment"; body: string }
  | { action: "edit"; summary?: string; description?: string };

export async function projectJiraAction(
  projectId: string,
  key: string,
  body: ProjectJiraAction
): Promise<void> {
  await adminFetch(
    `/admin/projects/${encodeURIComponent(projectId)}/jira/${encodeURIComponent(key)}/action`,
    { method: "POST", body: JSON.stringify(body) }
  );
}

export async function projectJiraCreate(
  projectId: string,
  body: {
    summary: string;
    description?: string;
    issueType?: string;
    board?: string;
    initialStatus?: string;
    assigneeAccountId?: string;
    priority?: string;
  }
): Promise<{ key: string; url: string; status: string }> {
  const data = await adminFetch<{ issue: { key: string; url: string; status: string } }>(
    `/admin/projects/${encodeURIComponent(projectId)}/jira/create`,
    { method: "POST", body: JSON.stringify(body) }
  );
  return data.issue;
}

export interface JiraAssignableUser {
  accountId: string;
  displayName: string;
  avatarUrl?: string;
}

export async function listJiraAssignableUsers(query = ""): Promise<JiraAssignableUser[]> {
  const data = await adminFetch<{ users: JiraAssignableUser[] }>(
    `/admin/jira/users?query=${encodeURIComponent(query)}`
  );
  return data.users;
}

export async function supersedeDecision(
  projectId: string,
  decisionId: string,
  supersededBy: string
): Promise<Decision> {
  const data = await adminFetch<{ decision: Decision }>(
    `/admin/projects/${encodeURIComponent(projectId)}/decisions/${encodeURIComponent(decisionId)}`,
    { method: "POST", body: JSON.stringify({ action: "supersede", supersededBy }) }
  );
  return data.decision;
}

export interface PlanBundle {
  plan: {
    content: string;
    versionId: string;
    ordinal: number;
    versions: PlanVersionEntry[];
    viewingVersionId?: string;
    readOnly?: boolean;
  } | null;
  meta: PlanMeta | null;
  threads: PlanThread[];
  proposals: PlanProposal[];
  viewer: PlanViewer;
  proposalDetail?: unknown;
}

export async function getPlan(projectId: string, opts?: { proposal?: string }): Promise<PlanBundle> {
  const q = new URLSearchParams({ project: projectId });
  if (opts?.proposal) q.set("proposal", opts.proposal);
  return adminFetch<PlanBundle>(`/plan?${q}`);
}

export async function startPlan(body: {
  project: string;
  template?: boolean;
  fromKey?: string;
  /** Adopt an orphaned projects/<id>/plan.md already in the store as v1. */
  adopt?: boolean;
}): Promise<PlanBundle & { content?: string }> {
  return adminFetch("/plan/start", { method: "POST", body: JSON.stringify(body) });
}

export async function addPlanThread(body: {
  project: string;
  type: PlanThreadType;
  body: string;
  citations?: PlanCitation[];
  target?: { proposalId?: string; anchor?: { headingSlug?: string; quotedText?: string; docKey?: string } | null };
}): Promise<{ thread: PlanThread }> {
  return adminFetch("/plan/thread", { method: "POST", body: JSON.stringify(body) });
}

export async function replyPlanThread(body: {
  project: string;
  threadId: string;
  body: string;
  citations?: PlanCitation[];
}): Promise<{ thread: PlanThread }> {
  return adminFetch("/plan/thread/reply", { method: "POST", body: JSON.stringify(body) });
}

export async function setPlanThreadStatus(body: {
  project: string;
  threadId: string;
  status: "resolved" | "wontfix" | "open";
  reason?: string;
}): Promise<{ thread: PlanThread }> {
  return adminFetch("/plan/thread/status", { method: "POST", body: JSON.stringify(body) });
}

export async function reviewProposal(body: {
  project: string;
  proposalId: string;
  verdict: "approve" | "request_changes";
  comment?: string;
}): Promise<{ proposal: PlanProposal }> {
  return adminFetch("/plan/proposal/review", { method: "POST", body: JSON.stringify(body) });
}

export async function signOffProposal(body: {
  project: string;
  proposalId: string;
  overriddenThreads?: string[];
  resolutions?: Record<string, "proposed" | "current">;
}): Promise<{ meta: PlanMeta; proposal: PlanProposal; versionId: string; ordinal: number }> {
  return adminFetch("/plan/proposal/signoff", { method: "POST", body: JSON.stringify(body) });
}

export async function dismissProposal(body: {
  project: string;
  proposalId: string;
  reason: string;
}): Promise<{ proposal: PlanProposal }> {
  return adminFetch("/plan/proposal/dismiss", { method: "POST", body: JSON.stringify(body) });
}

export async function getPlanNudges(
  projectIds: string[]
): Promise<Record<string, { openProposals: number; awaitingMySignoff: number; oldestOpenDays: number | null }>> {
  const q = encodeURIComponent(projectIds.join(","));
  const data = await adminFetch<{ nudges: Record<string, { openProposals: number; awaitingMySignoff: number; oldestOpenDays: number | null }> }>(
    `/plan/nudges?projects=${q}`
  );
  return data.nudges ?? (data as unknown as Record<string, { openProposals: number; awaitingMySignoff: number; oldestOpenDays: number | null }>);
}

/** Inline edit → proposed change (WP-A; P3 adds why/addresses/decisions). */
export async function proposePlanEdit(body: {
  project: string;
  content: string;
  summary: string;
  baseVersionId: string;
  why?: string;
  addresses?: string[];
  decisions?: Array<{ statement: string; why?: string; alternatives?: string }>;
}): Promise<{ proposal: PlanProposal; stale: boolean }> {
  return adminFetch("/plan/propose", { method: "POST", body: JSON.stringify(body) });
}

/** Request plan review + Slack notify (WP-A). */
export async function requestPlanReview(
  projectId: string,
  body: {
    reviewers: Array<{ personId: string; displayName: string } | string>;
    deepLink?: string;
  }
): Promise<{ meta: PlanMeta; slack: { posted: boolean; reason?: string } }> {
  return adminFetch(`/admin/projects/${encodeURIComponent(projectId)}/plan/review-requests`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/** Approve / Request changes on the current plan version (WP-A). */
export async function submitPlanReviewVerdict(
  projectId: string,
  body: { verdict: "approve" | "request_changes"; comment?: string }
): Promise<{ meta: PlanMeta }> {
  return adminFetch(`/admin/projects/${encodeURIComponent(projectId)}/plan/review`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

// ---------------------------------------------------------------------------
// Internal Knowledge (docs/design/internal-knowledge) — human-only REST
// ---------------------------------------------------------------------------

export type FreshnessBand = "fresh" | "aging" | "stale" | "unknown";
export type PresenceState = "active" | "held-unpushed" | "idle";
export type LibraryDocType = "sop" | "runbook" | "how-to" | "overview";

export interface DepartmentRow {
  deptId: string;
  label: string;
  descr?: string;
  icon?: string;
  lead?: string | null;
  status: string;
  sortOrder: number;
  members?: Array<{ userId: string; slug?: string; displayName: string; avatarKey?: string }>;
  activeProjectCount?: number;
  libraryDocCount?: number;
  freshness?: Record<FreshnessBand, number>;
}

export interface PresenceSession {
  holder: string;
  holderLabel?: string;
  slug?: string;
  displayName?: string;
  avatarKey?: string;
  scope: string;
  project?: string;
  customer?: string;
  state: PresenceState;
  startedAt: number;
  lastPushedAt?: number | null;
  lastSeenAt?: number;
  leaseExpiresAt?: number;
}

export interface LibraryDocRow {
  docKey: string;
  scope: string;
  title: string;
  docType: LibraryDocType;
  owner?: string;
  verifiedAt?: string;
  pinned: boolean;
  sortOrder: number;
  status: string;
  tags?: string[];
  freshness?: FreshnessBand;
  /** Server-computed for the current viewer — never derive edit rights client-side. */
  canEdit?: boolean;
}

export interface LibraryViewer {
  isAdmin: boolean;
  /** scope → may the viewer create a new doc there. */
  canCreate: Record<string, boolean>;
}

export interface AeronetOverview {
  digest: {
    weekOf?: string | null;
    byline?: string;
    empty?: boolean;
    emptyMessage?: string;
    departments?: Array<{
      deptId: string;
      label?: string;
      bullets: Array<{ tag: string; text: string; refs?: string[] }>;
    }>;
    capturedCount?: number;
  };
  canon: Array<{ title: string; summary: string; key: string }>;
  kpis: {
    people: number;
    departments: number;
    activeProjects: number;
    libraryDocs: number;
    freshness: Record<FreshnessBand, number>;
    capturedThisWeek: number;
  };
  presence: { activeCount: number; sessions: PresenceSession[] };
}

export interface PersonGridRow {
  slug: string;
  userId: string;
  displayName: string;
  avatarUrl?: string;
  role: string;
  departments: string[];
  blurb?: string;
  pet?: { name?: string; avatar?: string; persona?: string } | null;
}

export async function getAeronetOverview(): Promise<AeronetOverview> {
  return adminFetch("/aeronet/overview");
}

export async function listDepartmentsApi(): Promise<DepartmentRow[]> {
  const data = await adminFetch<{ departments: DepartmentRow[] }>("/departments");
  return data.departments ?? [];
}

export async function getDepartmentApi(id: string): Promise<DepartmentRow> {
  const data = await adminFetch<{ department: DepartmentRow }>(`/departments/${encodeURIComponent(id)}`);
  return data.department;
}

export async function listPeopleApi(): Promise<PersonGridRow[]> {
  const data = await adminFetch<{ people: PersonGridRow[] }>("/people");
  return data.people ?? [];
}

export async function getPersonApi(slug: string): Promise<Record<string, unknown>> {
  const data = await adminFetch<{ person: Record<string, unknown> }>(`/people/${encodeURIComponent(slug)}`);
  return data.person;
}

export async function getPresence(): Promise<{ sessions: PresenceSession[]; activeCount: number }> {
  return adminFetch("/presence");
}

export async function listLibrary(
  scope?: string,
  tag?: string
): Promise<{
  docs: LibraryDocRow[];
  byScope: Record<string, LibraryDocRow[]>;
  byTag?: Record<string, LibraryDocRow[]>;
  tags?: string[];
  viewer?: LibraryViewer;
}> {
  const params = new URLSearchParams();
  if (scope) params.set("scope", scope);
  if (tag) params.set("tag", tag);
  const q = params.toString() ? `?${params}` : "";
  return adminFetch(`/library${q}`);
}

export async function getLibraryDocApi(key: string, versionId?: string): Promise<{
  doc: LibraryDocRow;
  content: string;
  versions: Array<{ versionId: string; lastModified?: string; isLatest?: boolean }>;
  related: Array<{ docKey: string; title: string; docType: string; freshness?: FreshnessBand }>;
}> {
  const params = new URLSearchParams({ key });
  if (versionId) params.set("versionId", versionId);
  return adminFetch(`/library/doc?${params}`);
}

export async function upsertLibraryDocApi(body: Record<string, unknown>): Promise<{
  doc: LibraryDocRow;
  versionId?: string;
}> {
  return adminFetch("/library/doc", { method: "POST", body: JSON.stringify(body) });
}

export async function patchLibraryMetaApi(body: Record<string, unknown>): Promise<{ doc: LibraryDocRow }> {
  return adminFetch("/library/doc/meta", { method: "POST", body: JSON.stringify(body) });
}

export async function updateMyProfile(patch: Record<string, unknown>): Promise<{ profile: unknown }> {
  return adminFetch("/me/profile", { method: "PUT", body: JSON.stringify(patch) });
}

export async function uploadMyAvatar(contentBase64: string, contentType: string): Promise<{
  avatarKey: string;
  avatarUrl?: string;
}> {
  return adminFetch("/me/avatar", {
    method: "POST",
    body: JSON.stringify({ contentBase64, contentType }),
  });
}

/** Non-admin project browse (Internal Knowledge Projects tab). */
export async function listProjectsPublic(opts?: {
  team?: string;
  status?: string;
}): Promise<Project[]> {
  const params = new URLSearchParams();
  if (opts?.team) params.set("team", opts.team);
  if (opts?.status) params.set("status", opts.status);
  const q = params.toString() ? `?${params}` : "";
  const data = await adminFetch<{ projects: Project[] }>(`/projects-list${q}`);
  return data.projects ?? [];
}

// ---------------------------------------------------------------------------
// Notification playground N1 — messages + inbox
// ---------------------------------------------------------------------------

export type MessageKind = "comment" | "thread" | "reply" | "system";

export interface BrainMessage {
  id: string;
  source: string;
  sourceId: string;
  kind: MessageKind;
  author: string;
  body: string;
  mentions: string[];
  replyTo?: string;
  anchor?: Record<string, unknown>;
  status?: string;
  createdAt: number;
  updatedAt?: number;
}

export type NotifyReason = "mentioned" | "reply" | "owner" | "lead" | "assignee";

export interface BrainNotification {
  eventId: string;
  userId: string;
  reason: NotifyReason;
  eventType: string;
  source: string;
  sourceId: string;
  actor: string;
  payload: Record<string, unknown>;
  ts: number;
  seenAt?: number;
  doneAt?: number;
  title?: string;
  url?: string;
}

export interface NotificationCount {
  unread: number;
  unseen: number;
  mentions: number;
  replies: number;
  reviews: number;
  other: number;
}

export async function listMessagesApi(source: string, sourceId: string): Promise<BrainMessage[]> {
  const q = `?source=${encodeURIComponent(source)}&sourceId=${encodeURIComponent(sourceId)}`;
  const data = await adminFetch<{ messages: BrainMessage[] }>(`/admin/messages${q}`);
  return data.messages ?? [];
}

export async function createMessageApi(input: {
  source: string;
  sourceId: string;
  body: string;
  mentions?: string[];
  replyTo?: string;
  kind?: MessageKind;
}): Promise<BrainMessage> {
  const data = await adminFetch<{ message: BrainMessage }>("/admin/messages", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return data.message;
}

export async function patchMessageApi(
  id: string,
  patch: { body?: string; status?: string; waitingOn?: string }
): Promise<BrainMessage> {
  const data = await adminFetch<{ message: BrainMessage }>(
    `/admin/messages/${encodeURIComponent(id)}`,
    { method: "PATCH", body: JSON.stringify(patch) }
  );
  return data.message;
}

// ── Correspondence Desk (C2) ────────────────────────────────────────────────

export type DeskLane = "open" | "waiting" | "resolved";

export interface DeskRow {
  rootId: string;
  source: string;
  sourceId: string;
  title: string;
  url: string;
  status: DeskLane;
  waitingOnMe: boolean;
  lastPreview: string;
  lastAuthor: string;
  lastActivityAt: number;
  unread: boolean;
}

export interface DeskData {
  lanes: Record<DeskLane, DeskRow[]>;
  total: number;
}

export async function getDeskApi(): Promise<DeskData> {
  const data = await adminFetch<DeskData>("/admin/desk");
  return {
    lanes: { open: data.lanes?.open ?? [], waiting: data.lanes?.waiting ?? [], resolved: data.lanes?.resolved ?? [] },
    total: data.total ?? 0,
  };
}

export async function setDeskMuteApi(source: string, sourceId: string, muted: boolean): Promise<void> {
  await adminFetch("/admin/desk/follow", { method: "POST", body: JSON.stringify({ source, sourceId, muted }) });
}

export async function markDeskSeenApi(source: string, sourceId: string): Promise<void> {
  await adminFetch("/admin/desk/seen", { method: "POST", body: JSON.stringify({ source, sourceId }) });
}

export async function listNotificationsApi(tab: "all" | "mentions" | "activity" = "all"): Promise<BrainNotification[]> {
  const data = await adminFetch<{ notifications: BrainNotification[] }>(
    `/admin/notifications?tab=${encodeURIComponent(tab)}`
  );
  return data.notifications ?? [];
}

export async function getNotificationCountApi(): Promise<NotificationCount> {
  return adminFetch<NotificationCount>("/admin/notifications/count");
}

export async function markNotificationsSeenApi(): Promise<{ marked: number }> {
  return adminFetch<{ marked: number }>("/admin/notifications/seen", { method: "POST", body: "{}" });
}

export async function markNotificationDoneApi(eventId: string): Promise<BrainNotification> {
  const data = await adminFetch<{ notification: BrainNotification }>(
    `/admin/notifications/${encodeURIComponent(eventId)}`,
    { method: "PATCH", body: "{}" }
  );
  return data.notification;
}

// Personal Today cockpit: Jira is the sole task source.
export interface PersonalWorkIssue extends ProjectJiraIssueRow { projectIds: string[] }
export interface PersonalWorkPage {
  projects: Project[];
  issues: PersonalWorkIssue[];
  pinnedProjectIds: string[];
  jiraLinked: boolean;
  nextPageToken: string | null;
  fetchedAt: string;
}
export function getPersonalWork(cursor?: string): Promise<PersonalWorkPage> {
  return adminFetch(`/me/work${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`);
}
